import React, { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import ContentBox from '../../components/ContentBox';
import HabboImage, { getHabboImageUrl } from '../../components/HabboImage';
import {
    logGameScore, canSubmitGameScore, submitGameScore,
    getGameLeaderboard, getLatestGameSubmissions, claimFranklyStamp,
} from '../../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const BUBBLE_SRC          = `${process.env.PUBLIC_URL}/img/games/lobbyburst/bubble.png`;
const FRANKLY_STAMP_SRC   = `${process.env.PUBLIC_URL}/img/stampcard_stamp.png`;
const HAND_SRC        = `${process.env.PUBLIC_URL}/img/games/lobbyburst/hand.png`;
const PADLOCK_SRC     = `${process.env.PUBLIC_URL}/img/games/lobbyburst/padlock.png`;
const POP_SRC         = `${process.env.PUBLIC_URL}/img/games/lobbyburst/pop.mp3`;
const MUSIC_SRC       = `${process.env.PUBLIC_URL}/img/games/lobbyburst/lobbyburst.mp3`;
const RAINBOW_SFX_SRC = `${process.env.PUBLIC_URL}/img/games/lobbyburst/rainbow.wav`;
const CLEARED_SFX_SRC = `${process.env.PUBLIC_URL}/img/games/lobbyburst/cleared.wav`;
const WOMP_SRC        = `${process.env.PUBLIC_URL}/img/games/lobbyburst/womp.mp3`;
const WINNER_SRC      = `${process.env.PUBLIC_URL}/img/games/lobbyburst/winner.mp3`;

const LOCK_SFX_SRC    = `${process.env.PUBLIC_URL}/img/games/lobbyburst/lock.mp3`;
const BG_LOBBY_SRC    = `${process.env.PUBLIC_URL}/img/games/lobbyburst/lobby.png`;
const BG_CLOSE_SRC    = `${process.env.PUBLIC_URL}/img/games/lobbyburst/lobby_close.png`;
const BG_BUBBLE_SRCS  = [1, 2, 3, 4].map(n => ({
    normal: `${process.env.PUBLIC_URL}/img/games/lobbyburst/bubble_${n}.png`,
    close:  `${process.env.PUBLIC_URL}/img/games/lobbyburst/bubble_${n}L.png`,
}));
// Mobile detection — used to reduce particle count and cap bg loop
const _isMobile = typeof navigator !== 'undefined' &&
    (/Mobi|Android/i.test(navigator.userAgent) || window.matchMedia('(max-width: 768px)').matches);
const MAX_BG_BUBBLES  = _isMobile ? 8 : 20;
const BG_BUBBLE_DUR   = 3000; // ms total travel time
const COLS         = 8;
const ROWS_INIT    = 7;
const BUBBLE_SIZE  = 42;
const RADIUS       = BUBBLE_SIZE / 2;
const ROW_HEIGHT   = Math.round(BUBBLE_SIZE * 0.866);
const CANVAS_W     = COLS * BUBBLE_SIZE;
const CANVAS_H     = 500;
const SHOOTER_Y    = CANVAS_H - 52;
const SHOOTER_X    = CANVAS_W / 2;
// Danger line: halfway up the NEXT queue bubbles
const DANGER_Y     = SHOOTER_Y - 61;
const SPEED        = 14;
const ROT_SPEED    = 0.016;
const POP_DELAY    = 55;   // ms between each bubble in a chain pop
const GAME_DURATION = 180; // seconds (3 minutes)

function rowInterval(secondsLeft) {
    if (secondsLeft > GAME_DURATION - 30) return null; // first 30s: no rows
    const t = secondsLeft / (GAME_DURATION - 30); // 1→0 as time runs out
    return Math.max(1000, 1000 + t * 19000);
}

const BUBBLE_COLORS = [
    { id: 'orange', hex: '#303030' }, // white
    { id: 'red',    hex: '#c92302' },
    { id: 'purple', hex: '#c41aae' }, // hot pink
    { id: 'yellow', hex: '#9e9602' }, // purple
    { id: 'blue',   hex: '#0965e6' },
    { id: 'green',  hex: '#21ad42' },
];

// Locked bubble
const LOCKED_COLOR = { id: 'locked', hex: '#0a0a0a' };
const LOCKED_SPAWN_DELAY = 60;

// Rainbow bubble
const RAINBOW_COLOR        = { id: 'rainbow',  hex: '#ff0000' };
const RAINBOW_BASE_COLOR   = { id: 'rb_base',  hex: '#c8c8c8' };
const RAINBOW_SPAWN_AFTER  = 90;
const RAINBOW_CHANCE       = 0.035;
const RAINBOW_BOOST_CHANCE = 0.10;

// Bomb bubble — appears in the shooter queue, not spawned in grid rows
const BOMB_COLOR       = { id: 'bomb', hex: '#2a0a0a' }; // very dark red-black base
const BOMB_SPAWN_AFTER  = 60;   // seconds elapsed before bombs can enter the queue
const BOMB_QUEUE_CHANCE = 0.04; // 4% per shot → ~1–2 bombs expected per game
const BOMB_FUSE_MS      = 10000; // 10 seconds after landing before explosion
const BOMB_CANNON_FUSE_MS = 10000; // 10 seconds on the cannon before forced freeze
const BOMB_FREEZE_MS      = 5000;  // 5 second control freeze if bomb expires on cannon

// ─── Audio pool ───────────────────────────────────────────────────────────────
// Helper — ensures every Audio element requests full preload for mobile
function _mkAudio(src) { const a = new Audio(src); a.preload = 'auto'; return a; }

// Pop pool — disabled on mobile (HTMLAudio pool scheduling causes jank on pop-heavy frames)
const POP_POOL_SIZE = _isMobile ? 0 : 12;
const _popPool = _isMobile ? [] : Array.from({ length: POP_POOL_SIZE }, () => {
    const a = _mkAudio(POP_SRC);
    a.volume = 0.35;
    return a;
});
let _popIdx   = 0;
let _sfxVol   = parseFloat(localStorage.getItem('lobbyburst_sfx')   ?? '1');
let _musicVol = parseFloat(localStorage.getItem('lobbyburst_music') ?? '0.25');

// Music track — single looping element
const _music = _mkAudio(MUSIC_SRC);
_music.loop   = true;
_music.volume = _musicVol;

function setSfxVol(v)   { _sfxVol = v; _popPool.forEach(a => { a.volume = 0.35 * v; }); }
function setMusicVol(v) { _musicVol = v; _music.volume = v; }
function startMusic()   { if (_music.paused) _music.play().catch(() => {}); }

// If autoplay is blocked, retry on the first user gesture
let _musicGestureListener = null;
function startMusicWithGestureFallback() {
    _music.play().catch(() => {
        if (_musicGestureListener) return;
        _musicGestureListener = () => {
            if (_music.paused) _music.play().catch(() => {});
            ['click','keydown','touchstart'].forEach(e =>
                document.removeEventListener(e, _musicGestureListener, { once: true })
            );
            _musicGestureListener = null;
        };
        ['click','keydown','touchstart'].forEach(e =>
            document.addEventListener(e, _musicGestureListener, { once: true })
        );
    });
}
function stopMusic() { _music.pause(); _music.currentTime = 0; }

// One-shot SFX — single instances, re-triggered by resetting currentTime
const _sfxRainbow = _mkAudio(RAINBOW_SFX_SRC);
const _sfxCleared = _mkAudio(CLEARED_SFX_SRC);
const _sfxWinner  = _mkAudio(WINNER_SRC);

// Lock SFX — small pool so rapid locking events (bomb explosion) don't cut each other off
const LOCK_POOL_SIZE = 3;
const _lockPool = Array.from({ length: LOCK_POOL_SIZE }, () => _mkAudio(LOCK_SFX_SRC));
let _lockIdx = 0;

function _playSfx(a) {
    if (_isMobile || _sfxVol === 0) return;
    a.volume = _sfxVol;
    try { a.currentTime = 0; a.play().catch(() => {}); } catch (_) {}
}
function playRainbowSfx() { _playSfx(_sfxRainbow); }
function playClearedSfx() { _playSfx(_sfxCleared); }
function playWinnerSfx()  { _playSfx(_sfxWinner); }
function playLockSfx() {
    if (_isMobile || _sfxVol === 0) return;
    const a = _lockPool[_lockIdx % LOCK_POOL_SIZE];
    _lockIdx++;
    a.volume = _sfxVol;
    try { a.currentTime = 0; a.play().catch(() => {}); } catch (_) {}
}

let _fadeInterval = null;
function fadeMusicOut(durationMs = 1500) {
    if (_fadeInterval) clearInterval(_fadeInterval);
    const steps    = 30;
    const stepMs   = durationMs / steps;
    const startVol = _music.volume;
    let   step     = 0;
    _fadeInterval = setInterval(() => {
        step++;
        _music.volume = Math.max(0, startVol * (1 - step / steps));
        if (step >= steps) {
            clearInterval(_fadeInterval);
            _fadeInterval = null;
            _music.pause();
            // Restore volume for next play
            _music.volume = _musicVol;
        }
    }, stepMs);
}

function restartMusic() {
    if (_fadeInterval) { clearInterval(_fadeInterval); _fadeInterval = null; }
    _music.pause();
    _music.currentTime = 0;
    _music.volume      = _musicVol;
    _music.play().catch(() => {});
}

function playPop() {
    if (_isMobile || _sfxVol === 0 || !POP_POOL_SIZE) return;
    const a = _popPool[_popIdx % POP_POOL_SIZE];
    _popIdx++;
    if (!a) return;
    try { a.currentTime = 0; a.play().catch(() => {}); } catch (_) {}
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function randomColor(pool) {
    const p = pool && pool.length ? pool : BUBBLE_COLORS;
    return p[Math.floor(Math.random() * p.length)];
}

function randomColorForQueue(history, maxRun = 4) {
    const last = history[history.length - 1];
    const runLength = history.slice(-maxRun).filter(c => c?.id === last?.id).length;
    const pool = (runLength >= maxRun)
        ? BUBBLE_COLORS.filter(c => c.id !== last.id)
        : BUBBLE_COLORS;
    return pool[Math.floor(Math.random() * pool.length)];
}

function activeColors(grid) {
    const ids = new Set();
    for (const row of grid) for (const b of (row || [])) if (b) ids.add(b.id);
    return BUBBLE_COLORS.filter(c => ids.has(c.id));
}

function bubbleX(col, row, topParity = 0) {
    return col * BUBBLE_SIZE + RADIUS + ((row + topParity) % 2 === 1 ? BUBBLE_SIZE / 2 : 0);
}
function bubbleY(row) { return row * ROW_HEIGHT + RADIUS + 2; }
function dist(ax, ay, bx, by) { return Math.hypot(ax - bx, ay - by); }

function makeRow(parity, pool) {
    const cols = parity === 0 ? COLS : COLS - 1;
    return Array.from({ length: cols }, () => randomColor(pool));
}

function initGrid() {
    return Array.from({ length: ROWS_INIT }, (_, r) => makeRow(r % 2, BUBBLE_COLORS));
}

function getNeighbours(r, c, grid, topParity = 0) {
    const odd = (r + topParity) % 2 === 1;
    return [
        [r - 1, odd ? c     : c - 1],
        [r - 1, odd ? c + 1 : c    ],
        [r,     c - 1],
        [r,     c + 1],
        [r + 1, odd ? c     : c - 1],
        [r + 1, odd ? c + 1 : c    ],
    ].filter(([nr, nc]) =>
        nr >= 0 && nr < grid.length &&
        nc >= 0 && nc < (grid[nr]?.length || 0) &&
        grid[nr][nc]
    );
}

// BFS with index pointer — avoids O(n) array mutations from queue.shift()
function findConnected(grid, row, col, topParity = 0) {
    const target = grid[row]?.[col];
    if (!target) return [];
    const visited = new Set();
    const queue   = [[row, col]];
    const result  = [];
    let qi = 0;
    while (qi < queue.length) {
        const [r, c] = queue[qi++];
        const key = `${r},${c}`;
        if (visited.has(key)) continue;
        if (!grid[r]?.[c] || grid[r][c].id !== target.id) continue;
        visited.add(key); result.push([r, c]);
        for (const nb of getNeighbours(r, c, grid, topParity))
            if (!visited.has(`${nb[0]},${nb[1]}`)) queue.push(nb);
    }
    return result;
}

function findFloating(grid, topParity = 0) {
    const attached = new Set();
    const queue = [];
    for (let c = 0; c < (grid[0]?.length || 0); c++) {
        if (grid[0]?.[c]) { queue.push([0, c]); attached.add(`0,${c}`); }
    }
    let qi = 0;
    while (qi < queue.length) {
        const [r, c] = queue[qi++];
        for (const [nr, nc] of getNeighbours(r, c, grid, topParity)) {
            const key = `${nr},${nc}`;
            if (!attached.has(key)) { attached.add(key); queue.push([nr, nc]); }
        }
    }
    const floating = [];
    for (let r = 0; r < grid.length; r++)
        for (let c = 0; c < (grid[r]?.length || 0); c++)
            if (grid[r][c] && !attached.has(`${r},${c}`)) floating.push([r, c]);
    return floating;
}

function computeAimLine(angle) {
    const pts = [{ x: SHOOTER_X, y: SHOOTER_Y }];
    let x = SHOOTER_X, y = SHOOTER_Y;
    let dx = Math.cos(angle) * 14, dy = Math.sin(angle) * 14;
    for (let i = 0; i < 80; i++) {
        x += dx; y += dy;
        if (x <= RADIUS)            { x = RADIUS;            dx =  Math.abs(dx); }
        if (x >= CANVAS_W - RADIUS) { x = CANVAS_W - RADIUS; dx = -Math.abs(dx); }
        pts.push({ x, y });
        if (y < 0) break;
    }
    return pts;
}

// ─── Tint cache ───────────────────────────────────────────────────────────────

const _tintCache = new Map();

function getTinted(img, color, size) {
    const key = `${color.id}-${size}`;
    if (_tintCache.has(key)) return _tintCache.get(key);

    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const octx = off.getContext('2d');
    octx.imageSmoothingEnabled = false;
    octx.drawImage(img, 0, 0, size, size);

    const cr = parseInt(color.hex.slice(1, 3), 16);
    const cg = parseInt(color.hex.slice(3, 5), 16);
    const cb = parseInt(color.hex.slice(5, 7), 16);

    const imageData = octx.getImageData(0, 0, size, size);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        const brightness = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
        if (brightness <= 0.5) {
            const t = brightness * 2;
            data[i]     = Math.round(cr * t);
            data[i + 1] = Math.round(cg * t);
            data[i + 2] = Math.round(cb * t);
        } else {
            const t = (brightness - 0.5) * 2;
            data[i]     = Math.round(cr + (255 - cr) * t);
            data[i + 1] = Math.round(cg + (255 - cg) * t);
            data[i + 2] = Math.round(cb + (255 - cb) * t);
        }
    }

    octx.putImageData(imageData, 0, 0);
    _tintCache.set(key, off);
    return off;
}

function drawBubble(ctx, img, x, y, color, size, alpha = 1) {
    if (!img) return;
    ctx.globalAlpha = alpha;
    ctx.drawImage(getTinted(img, color, size), x - size / 2, y - size / 2, size, size);
    ctx.globalAlpha = 1;
}

// White pixel-art outline — draw the sprite as a white silhouette at 8-directional
// offsets behind the actual bubble. 2px offset matches the art-pixel scale.
const _blackSilhouetteCache = new Map();
function getBlackSilhouette(img, size) {
    const key = `black-sil-${size}`;
    if (_blackSilhouetteCache.has(key)) return _blackSilhouetteCache.get(key);
    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const octx = off.getContext('2d');
    octx.imageSmoothingEnabled = false;
    // Fill solid black, then clip to the sprite's alpha shape
    octx.fillStyle = '#000000';
    octx.fillRect(0, 0, size, size);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(img, 0, 0, size, size);
    octx.globalCompositeOperation = 'source-over';
    _blackSilhouetteCache.set(key, off);
    return off;
}

const _OUTLINE_OFFSETS = [[-4,0],[4,0],[0,-4],[0,4],[-4,-4],[4,-4],[-4,4],[4,4]];
function drawCannonOutline(ctx, img, x, y, color, size) {
    if (!img) return;
    const silhouette = getBlackSilhouette(img, size);
    for (const [dx, dy] of _OUTLINE_OFFSETS) {
        ctx.drawImage(silhouette, x - size / 2 + dx, y - size / 2 + dy, size, size);
    }
    drawBubble(ctx, img, x, y, color, size);
}

function drawLockIcon(ctx, x, y, size, padlockImg) {
    if (!padlockImg) return;
    const iw = padlockImg.naturalWidth;
    const ih = padlockImg.naturalHeight;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(padlockImg, Math.round(x - iw / 2), Math.round(y - ih / 2), iw, ih);
}

// Bomb base — pixel brightness crushed to 0–18% range so even highlights are near-black.
// Separate cache key so normal tintCache is unaffected.
const _bombBaseCache = new Map();
function getBombBase(img, size) {
    const key = `bomb-${size}`;
    if (_bombBaseCache.has(key)) return _bombBaseCache.get(key);

    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const octx = off.getContext('2d');
    octx.imageSmoothingEnabled = false;
    octx.drawImage(img, 0, 0, size, size);

    const imageData = octx.getImageData(0, 0, size, size);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        // Perceptual brightness 0–1
        const b = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
        // Crush to 0–0.18 range so even the specular highlight is very dark
        const out = b * 0.18;
        // Slight warm-dark tint (dark charcoal with a hint of red)
        data[i]     = Math.round(out * 200); // R slightly boosted
        data[i + 1] = Math.round(out * 140);
        data[i + 2] = Math.round(out * 140);
    }
    octx.putImageData(imageData, 0, 0);
    _bombBaseCache.set(key, off);
    return off;
}

// Red flash layer — same crush remap but pushed bright red
const _bombRedCache = new Map();
function getBombRed(img, size) {
    const key = `bomb-red-${size}`;
    if (_bombRedCache.has(key)) return _bombRedCache.get(key);

    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const octx = off.getContext('2d');
    octx.imageSmoothingEnabled = false;
    octx.drawImage(img, 0, 0, size, size);

    const imageData = octx.getImageData(0, 0, size, size);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 0) continue;
        const b = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
        // Map brightness to vivid red range (0.4–1.0) so it reads bright even at low flash alpha
        const out = 0.4 + b * 0.6;
        data[i]     = Math.round(out * 255);  // full red
        data[i + 1] = Math.round(out * 20);   // almost no green
        data[i + 2] = Math.round(out * 10);   // almost no blue
    }
    octx.putImageData(imageData, 0, 0);
    _bombRedCache.set(key, off);
    return off;
}

function drawBombBubble(ctx, img, x, y, size, secsLeft, timestamp) {
    if (!img) return;
    const period = Math.max(250, 180 + secsLeft * 110);
    const flash  = 0.5 + 0.5 * Math.sin((timestamp / period) * Math.PI * 2);
    const urgent = secsLeft <= 3;

    // Dark crushed base
    ctx.drawImage(getBombBase(img, size), x - size / 2, y - size / 2, size, size);

    // Red flash — screen blend makes it pop brightly over the dark base
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = flash * (urgent ? 0.95 : 0.72);
    ctx.drawImage(getBombRed(img, size), x - size / 2, y - size / 2, size, size);
    ctx.restore();

    // Countdown number — large, white with red glow
    const fontSize = Math.round(size * 0.44);
    ctx.save();
    ctx.font         = `bold ${fontSize}px 'Jersey 10', monospace`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'alphabetic';
    const label   = String(secsLeft);
    const metrics = ctx.measureText(label);
    const textX   = x - metrics.width / 2 + metrics.width / 2; // explicit center
    const textY   = y + (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;
    ctx.shadowColor  = urgent ? `rgba(255,0,0,${0.6 + 0.4 * flash})` : 'rgba(200,0,0,0.8)';
    ctx.shadowBlur   = urgent ? 10 : 6;
    ctx.fillStyle    = '#ffffff';
    ctx.fillText(label, x, textY);
    ctx.shadowBlur   = 0;
    ctx.restore();
}

function drawBombQueued(ctx, img, x, y, size, timestamp) {
    if (!img) return;
    ctx.drawImage(getBombBase(img, size), x - size / 2, y - size / 2, size, size);

    // Slow idle pulse
    const pulse = 0.35 + 0.30 * Math.sin(timestamp / 700);
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = pulse;
    ctx.drawImage(getBombRed(img, size), x - size / 2, y - size / 2, size, size);
    ctx.restore();

    // ! indicator
    const fontSize = Math.round(size * 0.40);
    ctx.save();
    ctx.font         = `bold ${fontSize}px 'Jersey 10', monospace`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'alphabetic';
    const mBang  = ctx.measureText('!');
    const bangY  = y + (mBang.actualBoundingBoxAscent - mBang.actualBoundingBoxDescent) / 2;
    ctx.shadowColor  = 'rgba(220,0,0,0.85)';
    ctx.shadowBlur   = 7;
    ctx.fillStyle    = '#ff4422';
    ctx.fillText('!', x, bangY);
    ctx.shadowBlur   = 0;
    ctx.restore();
}

// Reusable offscreen canvas for rainbow gradient compositing — avoids per-frame alloc.
let _rainbowCanvas = null;

function drawRainbowBubble(ctx, img, x, y, size, timestamp) {
    if (!img) return;

    // Lazy-init offscreen buffer
    if (!_rainbowCanvas || _rainbowCanvas.width !== size) {
        _rainbowCanvas = document.createElement('canvas');
        _rainbowCanvas.width = size; _rainbowCanvas.height = size;
    }
    const off  = _rainbowCanvas;
    const octx = off.getContext('2d');
    octx.clearRect(0, 0, size, size);

    // Spinning conic gradient — full cycle every ~1.2s
    const spin = ((timestamp / 1200) % 1) * Math.PI * 2;
    const grad = octx.createConicGradient(spin, size / 2, size / 2);
    grad.addColorStop(0,    '#ff1e1e');
    grad.addColorStop(0.16, '#ff8c00');
    grad.addColorStop(0.33, '#f0e600');
    grad.addColorStop(0.50, '#1ed250');
    grad.addColorStop(0.67, '#006eff');
    grad.addColorStop(0.83, '#a000ff');
    grad.addColorStop(1,    '#ff1e1e');

    // Step 1: fill gradient across entire canvas
    octx.fillStyle = grad;
    octx.fillRect(0, 0, size, size);

    // Step 2: clip gradient to bubble shape using destination-in
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(img, 0, 0, size, size);
    octx.globalCompositeOperation = 'source-over';

    // Step 3: draw bubble again with screen blend to restore white highlights/sheen
    octx.globalCompositeOperation = 'screen';
    octx.drawImage(img, 0, 0, size, size);
    octx.globalCompositeOperation = 'source-over';

    // Blit to main canvas
    ctx.drawImage(off, x - size / 2, y - size / 2);
}

// ─── Frankly bubble ───────────────────────────────────────────────────────────

const FRANKLY_COLOR = { id: 'frankly', hex: '#5A2929' };
// Spawn timing: divide the playable window [10s–170s elapsed] into 6 equal blocks,
// pick one block at random, then pick a random second within that block.
// This guarantees an even spread across the full game rather than clustering early.
const FRANKLY_WINDOW_START = 10;
const FRANKLY_WINDOW_END   = 170;
const FRANKLY_BLOCKS       = 6;
function franklySpawnElapsed() {
    const blockSize = (FRANKLY_WINDOW_END - FRANKLY_WINDOW_START) / FRANKLY_BLOCKS;
    const block     = Math.floor(Math.random() * FRANKLY_BLOCKS);
    return FRANKLY_WINDOW_START + block * blockSize + Math.random() * blockSize;
}

const _franklySilCache = new Map();
function getFranklySilhouette(img, size) {
    const key = `frankly-sil-${size}`;
    if (_franklySilCache.has(key)) return _franklySilCache.get(key);
    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const octx = off.getContext('2d');
    octx.imageSmoothingEnabled = false;
    // Solid maroon fill clipped to sprite alpha — no shading
    octx.fillStyle = '#5A2929';
    octx.fillRect(0, 0, size, size);
    octx.globalCompositeOperation = 'destination-in';
    octx.drawImage(img, 0, 0, size, size);
    octx.globalCompositeOperation = 'source-over';
    _franklySilCache.set(key, off);
    return off;
}

// Flat off-white stamp sprite clipped to the bubble alpha — no pixel loop needed.
const _franklyStampCache = new Map();
function getFranklyStampOverlay(bubbleImg, stampImg, size) {
    const key = `frankly-stamp-${size}`;
    if (_franklyStampCache.has(key)) return _franklyStampCache.get(key);

    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const octx = off.getContext('2d');

    // Fill off-white, clip to stamp sprite's own alpha (preserves stamp shape)
    octx.fillStyle = '#F2EDE3';
    octx.fillRect(0, 0, size, size);
    const stampSize = Math.round(size * 0.88);
    const pad = Math.round((size - stampSize) / 2);
    octx.globalCompositeOperation = 'destination-in';
    octx.imageSmoothingEnabled = true;
    octx.imageSmoothingQuality = 'high';
    octx.drawImage(stampImg, pad, pad, stampSize, stampSize);

    // Clip result to the bubble sprite's alpha
    octx.globalCompositeOperation = 'destination-in';
    octx.imageSmoothingEnabled = false;
    octx.drawImage(bubbleImg, 0, 0, size, size);
    octx.globalCompositeOperation = 'source-over';

    _franklyStampCache.set(key, off);
    return off;
}

function drawFranklyBubble(ctx, bubbleImg, stampImg, x, y, size) {
    if (!bubbleImg) return;
    ctx.drawImage(getFranklySilhouette(bubbleImg, size), x - size / 2, y - size / 2, size, size);
    if (stampImg) {
        ctx.drawImage(getFranklyStampOverlay(bubbleImg, stampImg, size), x - size / 2, y - size / 2, size, size);
    }
}

// ─── Scanlines cache ──────────────────────────────────────────────────────────
// Built once at module level and reused every frame — replaces N fillRect calls per frame.
let _scanlinesCanvas = null;
function getScanlines() {
    if (_scanlinesCanvas) return _scanlinesCanvas;
    const off = document.createElement('canvas');
    off.width = CANVAS_W; off.height = CANVAS_H;
    const octx = off.getContext('2d');
    octx.fillStyle = 'rgba(0,0,0,0.07)';
    for (let sy = 0; sy < CANVAS_H; sy += 4) octx.fillRect(0, sy, CANVAS_W, 2);
    _scanlinesCanvas = off;
    return off;
}

// ─── Particles ────────────────────────────────────────────────────────────────

function makePopParticles(x, y, color) {
    const count = _isMobile ? 6 : 14;
    return Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
        const speed = 1.6 + Math.random() * 3.4;
        return { x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 1.0,
            life: 1, decay: 0.020 + Math.random() * 0.002, color: color.hex, r: 3 + Math.random() * 3 };
    });
}

// ─── Timer helpers ────────────────────────────────────────────────────────────

function formatTime(secs) {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

// Colour for the timer text: white → yellow → red as time runs out
function timerColor(secs) {
    if (secs > 60) return 'rgba(255,255,255,0.85)';
    if (secs > 30) return 'rgba(255,200,60,0.9)';
    return 'rgba(220,60,60,0.95)';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LobbyBurst({ userData }) {
    const canvasRef    = useRef(null);
    const ctxRef       = useRef(null);    // cached main canvas 2d context
    const stateRef     = useRef(null);
    const rafRef       = useRef(null);
    const lastFrameTimeRef = useRef(0);   // for 60fps cap
    const bgLastFrameRef   = useRef(0);   // for bg 30fps cap
    const imgRef       = useRef(null);
    const handImgRef   = useRef(null);
    const padlockImgRef    = useRef(null);
    const franklyStampImgRef = useRef(null);
    const keysRef      = useRef({ left: false, right: false, fire: false, prevFire: false });
    const prevAngleRef = useRef(null);    // last rendered angle — aim line is only recomputed on change
    const avatarUrlsRef = useRef([]);     // pre-built avatar variant URLs [std/2, std/3, wav/2, wav/3]

    // Background layer refs
    const bgCanvasRef     = useRef(null);
    const bgCtxRef        = useRef(null);  // cached bg canvas 2d context
    const bgBubblesRef    = useRef([]);
    const bgRafRef        = useRef(null);
    const bgLoopRef       = useRef(null);  // stable ref to bg loop fn for visibility restart
    const bgBubbleImgsRef = useRef({ normal: [], close: [] });
    const bgSizeRef       = useRef({ w: 0, h: 0 }); // maintained by ResizeObserver, no per-frame DOM read

    const [score,       setScore]       = useState(0);
    const [lives,       setLives]       = useState(3);
    const [level,       setLevel]       = useState(1);
    const [phase,       setPhase]       = useState('idle');
    const [nextColor,   setNextColor]   = useState(null);
    const [timeLeft,    setTimeLeft]    = useState(GAME_DURATION);
    const [assetsReady, setAssetsReady] = useState(false);
    const [avatarState, setAvatarState] = useState({ gesture: 'std', action: 'std', headDir: 2 });
    const [avatarJumping, setAvatarJumping] = useState(false);
    const [jumpKey, setJumpKey] = useState(0);
    const [lbMode,      setLbMode]      = useState('winners');
    const [sfxVol,      setSfxVolState]  = useState(() => parseFloat(localStorage.getItem('lobbyburst_sfx')   ?? '1'));
    const [musicVol,    setMusicVolState] = useState(() => parseFloat(localStorage.getItem('lobbyburst_music') ?? '0.25'));
    const [audioMenuOpen, setAudioMenuOpen] = useState(false);
    const audioMenuRef = useRef(null);
    // Ref so the game loop (useCallback) can fire the stamp claim without stale closures
    const userDataRef = useRef(userData);
    useEffect(() => { userDataRef.current = userData; }, [userData]);

    // ── Games / leaderboard state ──────────────────────────────────────────────
    const [pendingScoreId,  setPendingScoreId]  = useState(null);
    const [userCanSubmit,   setUserCanSubmit]    = useState(null);
    const [submitModalOpen, setSubmitModalOpen]  = useState(false);
    const [submitting,      setSubmitting]       = useState(false);
    const [leaderboardWin,  setLeaderboardWin]   = useState([]);
    const [leaderboardLose, setLeaderboardLose]  = useState([]);
    const [submissions,     setSubmissions]      = useState([]);
    // Close audio menu when clicking outside
    useEffect(() => {
        if (!audioMenuOpen) return;
        const handler = (e) => {
            if (audioMenuRef.current && !audioMenuRef.current.contains(e.target)) {
                setAudioMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [audioMenuOpen]);
    const avatarWaveTimerRef = useRef(null);
    const avatarHeadTimerRef = useRef(null);
    const avatarJumpTimerRef = useRef(null);
    // Sync module-level audio on mount
    useEffect(() => { setSfxVol(_sfxVol); setMusicVol(_musicVol); }, []);

    // ── Fetch leaderboard + submissions on mount / when user changes ───────────
    useEffect(() => {
        getGameLeaderboard('winners').then(d => { if (d.success) setLeaderboardWin(d.leaderboard); }).catch(() => {});
        getGameLeaderboard('lose').then(d => { if (d.success) setLeaderboardLose(d.leaderboard); }).catch(() => {});
        getLatestGameSubmissions().then(d => { if (d.success) setSubmissions(d.submissions); }).catch(() => {});
        if (userData) {
            canSubmitGameScore().then(d => { if (d.success) setUserCanSubmit(d.canSubmit); }).catch(() => {});
        }
    }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Log score + refresh leaderboard on game end ────────────────────────────
    useEffect(() => {
        if (phase !== 'gameover' && phase !== 'win') return;
        const finalScore = stateRef.current?.score ?? 0;
        const outcome    = phase === 'win' ? 'win' : 'lose';

        const refreshLeaderboard = () => {
            getGameLeaderboard('winners').then(d => { if (d.success) setLeaderboardWin(d.leaderboard); }).catch(() => {});
            getGameLeaderboard('lose').then(d => { if (d.success) setLeaderboardLose(d.leaderboard); }).catch(() => {});
        };

        if (userData) {
            // Chain leaderboard refresh after logScore so the new score is in the DB first
            logGameScore(finalScore, outcome)
                .then(d => {
                    if (d.success) {
                        setPendingScoreId(d.scoreId);
                        if (d.isPersonalBest) toast.success('New High Score!');
                    }
                    refreshLeaderboard();
                })
                .catch(() => refreshLeaderboard());
            canSubmitGameScore()
                .then(d => { if (d.success) setUserCanSubmit(d.canSubmit); })
                .catch(() => {});
        } else {
            refreshLeaderboard();
        }
    }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Unified asset preload ──────────────────────────────────────────────────
    useEffect(() => {
        const bubbleImg    = new Image();
        const handImg      = new Image();
        const padlockImg   = new Image();
        const franklyStampImg = new Image();
        const bgNormal   = BG_BUBBLE_SRCS.map(() => new Image());
        const bgClose    = BG_BUBBLE_SRCS.map(() => new Image());
        const bgLobby    = new Image();
        const bgCloseImg = new Image();

        bgBubbleImgsRef.current = { normal: bgNormal, close: bgClose };

        const loadImg = (img, src) => new Promise(resolve => {
            img.onload = img.onerror = () => resolve();
            img.src = src;
        });

        const habboName = userData?.habboName || userData?.HabboName || 'Macklebee';
        const AVATAR_VARIANTS = [
            { gesture: 'std', action: 'std',  direction: 2, head_direction: 2 },
            { gesture: 'std', action: 'std',  direction: 2, head_direction: 3 },
            { gesture: 'sml', action: 'wav',  direction: 2, head_direction: 2 },
            { gesture: 'sml', action: 'wav',  direction: 2, head_direction: 3 },
        ];
        const avatarVariants = habboName ? AVATAR_VARIANTS.map(v => {
            const img = new Image();
            const src = getHabboImageUrl({ habbo: habboName, hotel: 'COM', size: 'a', shadow: false, ...v });
            return loadImg(img, src).then(() => src);
        }) : [];

        if (habboName) {
            Promise.all(avatarVariants).then(urls => { avatarUrlsRef.current = urls; });
        }

        const staticImgs = Promise.all([
            loadImg(bubbleImg,      BUBBLE_SRC),
            loadImg(handImg,        HAND_SRC),
            loadImg(padlockImg,     PADLOCK_SRC),
            loadImg(franklyStampImg, FRANKLY_STAMP_SRC),
            loadImg(bgLobby,        BG_LOBBY_SRC),
            loadImg(bgCloseImg,     BG_CLOSE_SRC),
            ...bgNormal.map((img, i) => loadImg(img, BG_BUBBLE_SRCS[i].normal)),
            ...bgClose.map( (img, i) => loadImg(img, BG_BUBBLE_SRCS[i].close)),
            ...avatarVariants,
        ]);

        const minDelay = new Promise(resolve => setTimeout(resolve, 2000));

        // Kick off audio preload in parallel (browsers can decode while images load)
        [..._lockPool, _sfxRainbow, _sfxCleared, _sfxWinner].forEach(a => {
            try { a.load(); } catch (_) {}
        });

        Promise.all([staticImgs, minDelay]).then(() => {
            if (bubbleImg.complete && bubbleImg.naturalWidth) {
                imgRef.current = bubbleImg;
                _tintCache.clear(); _bombBaseCache.clear(); _bombRedCache.clear(); _franklyStampCache.clear(); _franklySilCache.clear();
                // Pre-warm tint cache for all standard colours so first frame has no stalls
                [...BUBBLE_COLORS, LOCKED_COLOR, RAINBOW_BASE_COLOR].forEach(c => {
                    getTinted(bubbleImg, c, BUBBLE_SIZE);
                    getTinted(bubbleImg, c, 21); // small queue size
                });
                getBombBase(bubbleImg, BUBBLE_SIZE);
                getBombRed(bubbleImg, BUBBLE_SIZE);
                getBlackSilhouette(bubbleImg, BUBBLE_SIZE); // pre-warm cannon outline silhouette
                getFranklySilhouette(bubbleImg, BUBBLE_SIZE); // pre-warm frankly silhouette
            }
            if (handImg.complete && handImg.naturalWidth) {
                handImgRef.current = handImg;
            }
            if (padlockImg.complete && padlockImg.naturalWidth) {
                padlockImgRef.current = padlockImg;
            }
            if (franklyStampImg.complete && franklyStampImg.naturalWidth) {
                franklyStampImgRef.current = franklyStampImg;
                // Pre-warm the stamp overlay if bubble img is also ready
                if (imgRef.current) getFranklyStampOverlay(imgRef.current, franklyStampImg, BUBBLE_SIZE);
            }
            setAssetsReady(true);
        });
    }, []);

    // ── ResizeObserver for bg canvas ──────────────────────────────────────────
    useEffect(() => {
        const canvas = bgCanvasRef.current;
        if (!canvas?.parentElement) return;
        const seed = canvas.parentElement.getBoundingClientRect();
        if (seed.width && seed.height)
            bgSizeRef.current = { w: Math.round(seed.width), h: Math.round(seed.height) };
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width && height) bgSizeRef.current = { w: Math.round(width), h: Math.round(height) };
            }
        });
        ro.observe(canvas.parentElement);
        return () => ro.disconnect();
    }, []);

    // ── Background bubble animation ────────────────────────────────────────────
    useEffect(() => {
        const isClose = phase === 'playing' || phase === 'cascading';
        bgBubblesRef.current = []; // clear existing bubbles on mode switch
        let lastSpawn = 0;

        const loop = (ts) => {
            const canvas = bgCanvasRef.current;
            if (!canvas) { bgRafRef.current = requestAnimationFrame(loop); return; }

            // Background is decorative — cap at 30fps on mobile to save CPU
            if (_isMobile) {
                const bgDelta = ts - bgLastFrameRef.current;
                if (bgDelta < 33) { bgRafRef.current = requestAnimationFrame(loop); return; }
                bgLastFrameRef.current = ts;
            }

            // Apply any pending size change from ResizeObserver (no layout read)
            const { w, h } = bgSizeRef.current;
            if (w && h) {
                if (canvas.width  !== w) { canvas.width  = w; bgCtxRef.current = null; }
                if (canvas.height !== h) { canvas.height = h; bgCtxRef.current = null; }
            }

            const cw = canvas.width;
            const ch = canvas.height;
            if (!cw || !ch) { bgRafRef.current = requestAnimationFrame(loop); return; }

            // Cache context — set imageSmoothingEnabled once
            if (!bgCtxRef.current) {
                bgCtxRef.current = canvas.getContext('2d');
                if (bgCtxRef.current) bgCtxRef.current.imageSmoothingEnabled = false;
            }
            const ctx = bgCtxRef.current;
            if (!ctx) { bgRafRef.current = requestAnimationFrame(loop); return; }

            ctx.clearRect(0, 0, cw, ch);

            const imgSet = isClose ? bgBubbleImgsRef.current.close : bgBubbleImgsRef.current.normal;

            // Spawn a new bubble at a natural interval
            const spawnInterval = 320 + Math.random() * 280;
            if (ts - lastSpawn > spawnInterval && bgBubblesRef.current.length < MAX_BG_BUBBLES) {
                lastSpawn = ts;
                const idx    = Math.floor(Math.random() * 4);
                const spread = isClose ? cw * 0.10 : cw * 0.02;
                const spawnX = cw * 0.50 + (Math.random() - 0.5) * 2 * spread;
                const spawnY = isClose ? ch + 20 : ch * 0.78 + Math.random() * ch * 0.12;
                const endY   = isClose ? -60 : ch * 0.04;
                const dur    = BG_BUBBLE_DUR * (0.85 + Math.random() * 0.30);
                bgBubblesRef.current.push({ idx, x: spawnX, startY: spawnY, endY, createdAt: ts, dur });
            }

            // Update & draw — compact alive array in-place (no allocation)
            const bubs = bgBubblesRef.current;
            let alive = 0;
            for (let i = 0; i < bubs.length; i++) {
                const b = bubs[i];
                const t = (ts - b.createdAt) / b.dur; // 0 → 1
                if (t >= 1) continue; // expired — drop it

                const img = imgSet[b.idx];
                if (!img?.complete || !img.naturalWidth) { bubs[alive++] = b; continue; }

                const iw = Math.round(img.naturalWidth  / 2);
                const ih = Math.round(img.naturalHeight / 2);
                const y  = b.startY + t * (b.endY - b.startY);

                // Only draw if within canvas bounds
                if (y + ih >= 0 && y - ih <= ch) {
                    // Fade in for first 18%, full opacity through mid, fade out last 18%
                    let alpha;
                    if      (t < 0.18) alpha = t / 0.18;
                    else if (t > 0.82) alpha = (1 - t) / 0.18;
                    else               alpha = 1;
                    ctx.globalAlpha = alpha * 0.85;
                    ctx.drawImage(img, Math.round(b.x - iw / 2), Math.round(y - ih / 2), iw, ih);
                    ctx.globalAlpha = 1;
                }

                bubs[alive++] = b;
            }
            bubs.length = alive;

            bgRafRef.current = requestAnimationFrame(loop);
        };

        bgLoopRef.current = loop;
        bgRafRef.current  = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(bgRafRef.current);
            bgCtxRef.current  = null;
            bgLoopRef.current = null;
        };
    }, [phase]);

    // ── Build game state ───────────────────────────────────────────────────────
    const startGame = useCallback(() => {
        _tintCache.clear(); _bombBaseCache.clear(); _bombRedCache.clear(); _franklyStampCache.clear(); _franklySilCache.clear();
        const grid = initGrid();
        const shootColor   = randomColor(BUBBLE_COLORS);
        const nextCol      = randomColor(BUBBLE_COLORS);
        const nextNext     = randomColor(BUBBLE_COLORS);
        const nextNextNext = randomColor(BUBBLE_COLORS);
        const queueHistory = [shootColor, nextCol, nextNext, nextNextNext];

        stateRef.current = {
            grid,
            shootColor, nextColor: nextCol, nextNext, nextNextNext,
            queueHistory,
            projectile:   null,
            angle:       -Math.PI / 2,
            aimLine:      [],
            particles:    [],
            popQueue:     [],
            score: 0, lives: 3, level: 1,
            phase: 'playing',
            topParity: 0,
            // Timer
            timeLeft:  GAME_DURATION,
            lastTick:  null,
            // Row generation
            lastRowAt: null,
            // Gameover cascade state
            cascading:    false,
            cascadeQueue: [],
            // Locked bubbles
            risingLocked: [],
            nextLockedAt: null,
            // Rainbow tracking
            lastRowHadRainbow: false,
            rainbowForced:     false,
            bombForced:        false,
            // Bomb cannon timer — starts when bomb becomes active shoot bubble
            bombCannonTimer: null, // { startedAt: number }
            frozen:          false,
            frozenUntil:     null,
            recolourQueue:     [],   // [{ r, c, toColor, fireAt }]
            pendingConnect:    null, // { r, c, color, after } — triggers findConnected when recolours done
            // Multi-pop display
            multiDisplay:   null,
            clearedDisplay: null,
            timerAlert:     null,   // { text, life, color }
            scoreFloats:    [],     // [{ x, y, pts, life }]
            pendingClearRows: 0,
            nextClearRowAt:   null,
            // Frankly bubble — once per game
            franklySpawned:  false,
            franklySpawnAt:  null, // set after 1s of play; random elapsed between FRANKLY_SPAWN_MIN–MAX
        };
        prevAngleRef.current = null; // reset aim line cache
        restartMusic();
        setScore(0); setLives(3); setLevel(1);
        setNextColor(nextCol);
        setTimeLeft(GAME_DURATION);
        setPendingScoreId(null);
        setSubmitModalOpen(false);
        setPhase('playing');
    }, []);

    // ── Push a new row at the top, shifting everything down ───────────────────
    function pushNewRow(state, now, forceRainbow = false) {
        const grid = state.grid;
        const pool = activeColors(grid).length ? activeColors(grid) : BUBBLE_COLORS;
        const newTopParity = (state.topParity + 1) % 2;
        const newRow = makeRow(newTopParity, pool);
        // After RAINBOW_SPAWN_AFTER seconds, randomly replace some slots with rainbow
        const rainbowActive = (GAME_DURATION - state.timeLeft) >= RAINBOW_SPAWN_AFTER;
        let hadRainbow = false;
        if (rainbowActive || forceRainbow) {
            const chance = forceRainbow ? 1 : (state.lastRowHadRainbow ? RAINBOW_BOOST_CHANCE : RAINBOW_CHANCE);
            for (let i = 0; i < newRow.length; i++) {
                if (Math.random() < chance) { newRow[i] = RAINBOW_COLOR; hadRainbow = true; }
            }
            // forceRainbow guarantees at least one — pick a random slot if none landed
            if (forceRainbow && !hadRainbow) {
                const slot = Math.floor(Math.random() * newRow.length);
                newRow[slot] = RAINBOW_COLOR;
                hadRainbow = true;
            }
        }
        state.lastRowHadRainbow = hadRainbow;
        state.grid = [newRow, ...grid];
        state.topParity = newTopParity;
        state.lastRowAt = now;
        checkDangerLine(state, now);
    }

    // ── Check if any bubble has crossed into the danger zone ──────────────────
    function checkDangerLine(state, now) {
        if (state.cascading) return;
        for (let r = 0; r < state.grid.length; r++) {
            for (let c = 0; c < (state.grid[r]?.length || 0); c++) {
                if (state.grid[r][c] && bubbleY(r) > DANGER_Y) {
                    triggerCascade(state, now);
                    return;
                }
            }
        }
    }

    // ── Gameover cascade: pop all bubbles bottom-to-top ───────────────────────
    function triggerCascade(state, now) {
        state.cascading = true;
        state.phase = 'cascading';

        const all = [];
        for (let r = state.grid.length - 1; r >= 0; r--) {
            for (let c = 0; c < (state.grid[r]?.length || 0); c++) {
                if (state.grid[r][c]) {
                    all.push({ r, c, color: state.grid[r][c], x: bubbleX(c, r, state.topParity), y: bubbleY(r) });
                }
            }
        }

        let delay = 0;
        let lastRow = -1;
        for (const item of all) {
            if (item.r !== lastRow) { lastRow = item.r; delay += 30; }
            state.cascadeQueue.push({ ...item, fireAt: now + delay });
            delay += 18;
            state.grid[item.r][item.c] = null;
        }
    }

    // ── Shared helpers ────────────────────────────────────────────────────────

    // Returns the nearest empty grid cell to (px, py). Used by both snap functions.
    function findBestSnapCell(px, py, grid, topParity) {
        let bestRow = 0, bestCol = 0, bestDist = Infinity;
        const maxRow = Math.min(grid.length + 1, Math.ceil((py + BUBBLE_SIZE * 2) / ROW_HEIGHT) + 2);
        for (let r = 0; r <= maxRow; r++) {
            const cols = (r + topParity) % 2 === 0 ? COLS : COLS - 1;
            for (let c = 0; c < cols; c++) {
                if (grid[r]?.[c]) continue;
                const d = dist(px, py, bubbleX(c, r, topParity), bubbleY(r));
                if (d < bestDist) { bestDist = d; bestRow = r; bestCol = c; }
            }
        }
        return { row: Math.max(0, bestRow), col: bestCol };
    }

    // Rotates the shoot queue and appends a freshly-generated colour at the back.
    // Called after every bubble is placed (normal snap, bomb land, freeze unfreeze).
    function advanceQueue(state) {
        const elapsed = GAME_DURATION - state.timeLeft;
        const bombEligible = elapsed >= BOMB_SPAWN_AFTER;
        const nextNew = (bombEligible && Math.random() < BOMB_QUEUE_CHANCE)
            ? BOMB_COLOR : randomColorForQueue(state.queueHistory);
        if (nextNew.id !== 'bomb') state.queueHistory = [...state.queueHistory.slice(-7), nextNew];
        state.shootColor   = state.nextColor;
        state.nextColor    = state.nextNext     || randomColorForQueue(state.queueHistory);
        state.nextNext     = state.nextNextNext || randomColorForQueue(state.queueHistory);
        state.nextNextNext = nextNew;
        setNextColor(state.nextColor);
    }

    // ── Snap + pop logic ───────────────────────────────────────────────────────
    function snapAndPop(state, px, py, now) {
        const { grid, projectile, topParity } = state;
        if (!projectile) return;

        const { row: bestRow, col: bestCol } = findBestSnapCell(px, py, grid, topParity);

        while (grid.length <= bestRow) {
            const r = grid.length;
            grid.push(new Array((r + topParity) % 2 === 0 ? COLS : COLS - 1).fill(null));
        }

        // ── Bomb landing — behaves like locked: no colour match, only floats off ─
        if (projectile.color.id === 'bomb') {
            grid[bestRow][bestCol] = { ...BOMB_COLOR, isBomb: true, expiresAt: now + BOMB_FUSE_MS };
            state.projectile = null;
            checkDangerLine(state, now);
            advanceQueue(state);
            return;
        }

        grid[bestRow][bestCol] = projectile.color;
        state.projectile = null;

        // ── Rainbow recolour — staggered via recolourQueue ────────────────────
        // BFS collects the full rainbow cluster + outer ring; each cell gets a
        // fireAt so the grid updates one-by-one. findConnected runs only after
        // the last recolour fires (via state.pendingConnect in the game loop).
        const firedColor = grid[bestRow][bestCol];
        const rainbowVisited = new Set();
        const rainbowCluster = [];
        // Seed: rainbow/frankly neighbours of landing cell
        const _isRainbowLike = (cell) => cell?.id === 'rainbow' || cell?.isFrankly;
        let clusterHasFrankly = false;
        for (const [nr, nc] of getNeighbours(bestRow, bestCol, grid, topParity)) {
            const cell = grid[nr]?.[nc];
            if (_isRainbowLike(cell)) {
                const key = `${nr},${nc}`;
                if (!rainbowVisited.has(key)) {
                    rainbowVisited.add(key);
                    rainbowCluster.push([nr, nc]);
                    if (cell.isFrankly) clusterHasFrankly = true;
                }
            }
        }
        // BFS through touching rainbow/frankly bubbles
        let rqi = 0;
        while (rqi < rainbowCluster.length) {
            const [rr, rc] = rainbowCluster[rqi++];
            for (const [nr, nc] of getNeighbours(rr, rc, grid, topParity)) {
                const key = `${nr},${nc}`;
                const cell = grid[nr]?.[nc];
                if (_isRainbowLike(cell) && !rainbowVisited.has(key)) {
                    rainbowVisited.add(key);
                    rainbowCluster.push([nr, nc]);
                    if (cell.isFrankly) clusterHasFrankly = true;
                }
            }
        }

        if (rainbowCluster.length === 0) {
            // No rainbows/frankly touched — run pop logic immediately as normal
            tryPop(state, bestRow, bestCol, now);
        } else {
            // Collect outer ring (non-rainbow/frankly, non-locked neighbours of the whole cluster)
            const outerVisited = new Set([`${bestRow},${bestCol}`]);
            const outerRing = [];
            for (const [rr, rc] of rainbowCluster) {
                outerVisited.add(`${rr},${rc}`);
                for (const [nr, nc] of getNeighbours(rr, rc, grid, topParity)) {
                    const key = `${nr},${nc}`;
                    const cell = grid[nr]?.[nc];
                    if (cell && cell.id !== 'locked' && !_isRainbowLike(cell) && cell.id !== 'bomb' && !outerVisited.has(key)) {
                        outerVisited.add(key); outerRing.push([nr, nc]);
                    }
                }
            }
            // Queue staggered recolours: cluster first, then outer ring
            let delay = 0;
            for (const [rr, rc] of rainbowCluster) {
                state.recolourQueue.push({ r: rr, c: rc, toColor: firedColor, fireAt: now + delay });
                delay += POP_DELAY;
            }
            for (const [nr, nc] of outerRing) {
                state.recolourQueue.push({ r: nr, c: nc, toColor: firedColor, fireAt: now + delay });
                delay += POP_DELAY;
            }
            // After last recolour fires, run findConnected from the landing cell
            state.pendingConnect = { r: bestRow, c: bestCol, color: firedColor, after: now + delay };

            // Award stamp for hitting the Frankly bubble (logged-in users only, fire-and-forget)
            if (clusterHasFrankly) {
                if (userDataRef.current) {
                    claimFranklyStamp().catch(() => {});
                } else {
                    toast.info('Log in to earn framps!', { toastId: 'frankly-guest', autoClose: 3000 });
                }
            }
        }

        advanceQueue(state);
        checkDangerLine(state, now);
    }

    // ── Pop logic — called immediately (no rainbow) or deferred (post-recolour) ─
    function tryPop(state, originR, originC, now) {
        const { grid, topParity } = state;
        const connected = findConnected(grid, originR, originC, topParity);
        if (connected.length >= 3) {
            let delay = 0;
            for (const [r, c] of connected) {
                state.popQueue.push({ r, c, color: grid[r][c], x: bubbleX(c, r, topParity), y: bubbleY(r), fireAt: now + delay, pts: 10 });
                delay += POP_DELAY;
            }
            for (const [r, c] of connected) grid[r][c] = null;

            const floating = findFloating(grid, topParity);
            for (let fi = 0; fi < floating.length; fi++) {
                const [r, c] = floating[fi];
                const floatColor = grid[r][c];
                // Bombs cleared via floating: orange burst, no pts bonus
                const drawColor = floatColor.isBomb ? { hex: '#ff5500' } : floatColor;
                state.popQueue.push({ r, c, color: drawColor, x: bubbleX(c, r, topParity), y: bubbleY(r), fireAt: now + delay, pts: floatColor.isBomb ? 10 : 15 });
                delay += POP_DELAY;
                grid[r][c] = null;
            }

            const popped = connected.length + floating.length;
            state.score += popped * 10 + (popped > 3 ? (popped - 3) * 15 : 0);
            setScore(state.score);

            clearTimeout(avatarWaveTimerRef.current);
            setAvatarState(prev => ({ ...prev, gesture: 'sml', action: 'wav' }));
            avatarWaveTimerRef.current = setTimeout(() => {
                setAvatarState(prev => ({ ...prev, gesture: 'std', action: 'std' }));
            }, 2500);

            if (popped > 3) {
                clearTimeout(avatarJumpTimerRef.current);
                setJumpKey(k => k + 1);
                setAvatarJumping(true);
                avatarJumpTimerRef.current = setTimeout(() => setAvatarJumping(false), 960);
            }

            if (popped > 5) {
                state.multiDisplay = { count: popped, life: 1.0 };
            }
        }
    }

    // ── Snap a rising locked bubble into the grid ─────────────────────────────
    function snapLockedBubble(state, px, py, now, lb = null) {
        const { grid, topParity } = state;
        const { row: bestRow, col: bestCol } = findBestSnapCell(px, py, grid, topParity);
        while (grid.length <= bestRow) {
            const r = grid.length;
            grid.push(new Array((r + topParity) % 2 === 0 ? COLS : COLS - 1).fill(null));
        }
        grid[bestRow][bestCol] = lb?.isFrankly ? { ...FRANKLY_COLOR, isFrankly: true } : LOCKED_COLOR;
        if (!lb?.isFrankly) playLockSfx();
        checkDangerLine(state, now);
    }

    function doFire(state) {
        if (state.projectile || state.phase !== 'playing' || state.frozen) return;
        if (Math.sin(state.angle) > -0.08) return;
        if (state.shootColor?.id === 'bomb') state.bombCannonTimer = null;
        state.projectile = {
            x: SHOOTER_X, y: SHOOTER_Y,
            dx: Math.cos(state.angle) * SPEED,
            dy: Math.sin(state.angle) * SPEED,
            color: state.shootColor,
        };
    }

    // ── Game loop ──────────────────────────────────────────────────────────────
    const gameLoop = useCallback((timestamp) => {
        // ── 60fps cap — keeps physics correct on 90/120Hz mobile displays ────
        const frameDelta = timestamp - lastFrameTimeRef.current;
        if (frameDelta < 16) { rafRef.current = requestAnimationFrame(gameLoop); return; }
        lastFrameTimeRef.current = timestamp;

        const canvas = canvasRef.current;
        const state  = stateRef.current;
        const img    = imgRef.current;
        if (!canvas || !state) { rafRef.current = requestAnimationFrame(gameLoop); return; }

        // Cache context — imageSmoothingEnabled set once on acquisition
        if (!ctxRef.current) {
            ctxRef.current = canvas.getContext('2d');
            if (ctxRef.current) ctxRef.current.imageSmoothingEnabled = false;
        }
        const ctx = ctxRef.current;
        if (!ctx) { rafRef.current = requestAnimationFrame(gameLoop); return; }

        // ── Timer tick ────────────────────────────────────────────────────────
        if (state.phase === 'playing') {
            if (state.lastTick === null) state.lastTick = timestamp;
            const elapsed = (timestamp - state.lastTick) / 1000;
            if (elapsed >= 1) {
                const prevTime = state.timeLeft;
                state.timeLeft = Math.max(0, state.timeLeft - Math.floor(elapsed));
                state.lastTick = timestamp;
                setTimeLeft(state.timeLeft);
                if (state.timeLeft <= 0) {
                    state.phase = 'win';
                    setPhase('win');
                    playWinnerSfx();
                    fadeMusicOut(2000);
                }

                // Timer alert flashes
                const t = state.timeLeft;
                if ((prevTime > 30 && t <= 30) || (prevTime > 20 && t <= 20)) {
                    const is30 = prevTime > 30;
                    state.timerAlert = { text: `${is30 ? 30 : 20} SECONDS`, life: 1.0, color: is30 ? '#ffc840' : '#ff6020' };
                } else if (t <= 10 && t > 0 && t !== prevTime) {
                    state.timerAlert = { text: `${t}`, life: 1.0, color: '#e03030' };
                }

                // Force a rainbow row at exactly 1:30 remaining (90s left)
                if (prevTime > 90 && t <= 90 && !state.rainbowForced) {
                    state.rainbowForced = true;
                    pushNewRow(state, timestamp, true);
                }

                // Force a bomb into the queue at exactly 1:00 remaining (60s left)
                if (prevTime > 60 && t <= 60 && !state.bombForced) {
                    state.bombForced = true;
                    // Insert bomb at the front of the visible queue (nextColor = next to fire)
                    state.nextNextNext = state.nextNext;
                    state.nextNext     = state.nextColor;
                    state.nextColor    = BOMB_COLOR;
                    setNextColor(state.nextColor);
                }
            }

            // ── Row generation (paused during clear-bonus stagger) ───────────
            const interval = rowInterval(state.timeLeft);
            if (interval !== null && state.pendingClearRows === 0) {
                if (state.lastRowAt === null) state.lastRowAt = timestamp;
                if (timestamp - state.lastRowAt >= interval) pushNewRow(state, timestamp);
            }

            // ── Locked bubble spawning (after LOCKED_SPAWN_DELAY seconds) ────
            const lockedElapsed = GAME_DURATION - state.timeLeft;
            if (lockedElapsed >= LOCKED_SPAWN_DELAY) {
                if (state.nextLockedAt === null) {
                    state.nextLockedAt = timestamp + 3000 + Math.random() * 5000;
                }
                if (timestamp >= state.nextLockedAt) {
                    const col = 1 + Math.floor(Math.random() * (COLS - 2));
                    const spawnX = Math.max(RADIUS + 4, Math.min(CANVAS_W - RADIUS - 4,
                        bubbleX(col, 0, state.topParity) + (Math.random() - 0.5) * BUBBLE_SIZE * 0.5));
                    state.risingLocked.push({ x: spawnX, y: CANVAS_H + RADIUS, dy: -(2.2 + Math.random() * 1.4) });
                    state.nextLockedAt = timestamp + 10000 + Math.random() * 15000;
                }
            }

            // ── Frankly bubble spawn (once per game) ─────────────────────────
            if (!state.franklySpawned) {
                if (state.franklySpawnAt === null) {
                    const elapsed = GAME_DURATION - state.timeLeft;
                    if (elapsed >= 1) {
                        // Pick a random target elapsed second from a random block across the full game
                        const targetElapsed = franklySpawnElapsed();
                        const secsUntilSpawn = Math.max(2, targetElapsed - elapsed);
                        state.franklySpawnAt = timestamp + secsUntilSpawn * 1000;
                    }
                } else if (timestamp >= state.franklySpawnAt) {
                    state.franklySpawned = true;
                    if (Math.random() < 0.5) {
                        // Rise from bottom
                        const col = 1 + Math.floor(Math.random() * (COLS - 2));
                        const spawnX = Math.max(RADIUS + 4, Math.min(CANVAS_W - RADIUS - 4,
                            bubbleX(col, 0, state.topParity) + (Math.random() - 0.5) * BUBBLE_SIZE * 0.5));
                        state.risingLocked.push({ x: spawnX, y: CANVAS_H + RADIUS, dy: -(2.2 + Math.random() * 1.0), isFrankly: true });
                    } else {
                        // Push a normal new row then replace one random cell with the frankly bubble
                        pushNewRow(state, timestamp);
                        const newRow = state.grid[0];
                        if (newRow && newRow.length > 0) {
                            const col = Math.floor(Math.random() * newRow.length);
                            newRow[col] = { ...FRANKLY_COLOR, isFrankly: true };
                        }
                    }
                }
            }

            // ── Staggered clear-bonus rows ───────────────────────────────────
            if (state.pendingClearRows > 0 && state.nextClearRowAt !== null && timestamp >= state.nextClearRowAt) {
                pushNewRow(state, timestamp);
                state.pendingClearRows -= 1;
                state.nextClearRowAt = state.pendingClearRows > 0 ? timestamp + 500 : null;
            }
        }

        // ── Rising locked bubbles — movement + snap ───────────────────────────
        if (state.risingLocked.length > 0) {
            const stillRising = [];
            for (const lb of state.risingLocked) {
                lb.y += lb.dy;
                let snapped = false;
                if (lb.y <= RADIUS) {
                    snapLockedBubble(state, lb.x, lb.y, timestamp, lb);
                    snapped = true;
                } else {
                    for (let r = 0; r < state.grid.length && !snapped; r++) {
                        for (let c = 0; c < (state.grid[r]?.length || 0) && !snapped; c++) {
                            if (state.grid[r][c] && dist(lb.x, lb.y, bubbleX(c, r, state.topParity), bubbleY(r)) < BUBBLE_SIZE * 0.92) {
                                snapLockedBubble(state, lb.x, lb.y, timestamp, lb);
                                snapped = true;
                            }
                        }
                    }
                }
                if (!snapped) stillRising.push(lb);
            }
            state.risingLocked = stillRising;
        }

        // ── Bomb cannon timer ─────────────────────────────────────────────────
        if (state.phase === 'playing' && !state.frozen && !state.projectile) {
            if (state.shootColor?.id === 'bomb' && !state.bombCannonTimer) {
                state.bombCannonTimer = { startedAt: timestamp };
            }
            if (state.bombCannonTimer) {
                const fuseElapsed = timestamp - state.bombCannonTimer.startedAt;
                if (fuseElapsed >= BOMB_CANNON_FUSE_MS) {
                    state.bombCannonTimer = null;
                    state.frozen         = true;
                    state.frozenUntil    = timestamp + BOMB_FREEZE_MS;
                    playLockSfx(); // penalty signal — same sound as a bubble being locked
                }
            }
        }

        // ── Freeze tick (controls locked after bomb cannon expired) ───────────
        if (state.frozen) {
            // Unfreeze when period ends — advance queue past the bomb
            if (timestamp >= state.frozenUntil) {
                state.frozen      = false;
                state.frozenUntil = null;
                advanceQueue(state);
            }
        }

        // ── Input (only when actively playing) ───────────────────────────────
        if (state.phase === 'playing' && !state.frozen) {
            const keys = keysRef.current;
            if (keys.left)  state.angle = Math.max(state.angle - ROT_SPEED, -Math.PI + 0.12);
            if (keys.right) state.angle = Math.min(state.angle + ROT_SPEED, -0.12);
            if (keys.fire && !keys.prevFire) doFire(state);
            keys.prevFire = keys.fire;
            // Only recompute aim line when angle has actually changed
            if (state.angle !== prevAngleRef.current) {
                state.aimLine = computeAimLine(state.angle);
                prevAngleRef.current = state.angle;
            }
        }

        // ── Projectile movement ───────────────────────────────────────────────
        if (state.projectile) {
            state.projectile.x += state.projectile.dx;
            state.projectile.y += state.projectile.dy;
            if (state.projectile.x <= RADIUS)            { state.projectile.x = RADIUS;            state.projectile.dx =  Math.abs(state.projectile.dx); }
            if (state.projectile.x >= CANVAS_W - RADIUS) { state.projectile.x = CANVAS_W - RADIUS; state.projectile.dx = -Math.abs(state.projectile.dx); }
            if (state.projectile.y <= RADIUS) {
                snapAndPop(state, state.projectile.x, state.projectile.y, timestamp);
            } else {
                const vLen = Math.hypot(state.projectile.dx, state.projectile.dy);
                for (let r = 0; r < state.grid.length && state.projectile; r++) {
                    for (let c = 0; c < (state.grid[r]?.length || 0) && state.projectile; c++) {
                        if (!state.grid[r][c]) continue;
                        const bx = bubbleX(c, r, state.topParity);
                        const by = bubbleY(r);
                        // Tighter physical collision radius — only a genuine hit, not a graze
                        if (dist(state.projectile.x, state.projectile.y, bx, by) < BUBBLE_SIZE * 0.72) {
                            // Only snap if the projectile is heading fairly directly toward this bubble.
                            // cos > 0.5 ≈ within 60° — sidelong passes (gap-squeezing) are ignored.
                            const dvx = bx - state.projectile.x;
                            const dvy = by - state.projectile.y;
                            const dLen = Math.hypot(dvx, dvy);
                            const cosAngle = (state.projectile.dx * dvx + state.projectile.dy * dvy) / (vLen * dLen);
                            if (cosAngle > 0.50) {
                                snapAndPop(state, state.projectile.x, state.projectile.y, timestamp);
                            }
                        }
                    }
                }
            }
        }

        // ── Normal pop queue ──────────────────────────────────────────────────
        // On mobile, cap simultaneous pops per frame to avoid audio stutter
        const MAX_POPS_PER_FRAME = _isMobile ? 2 : 6;
        let popsThisFrame = 0;
        const stillPending = [];
        for (const item of state.popQueue) {
            if (timestamp >= item.fireAt) {
                state.particles.push(...makePopParticles(item.x, item.y, item.color));
                if (!_isMobile) state.scoreFloats.push({ x: item.x, y: item.y - RADIUS, pts: item.pts || 10, life: 1.0 });
                if (popsThisFrame < MAX_POPS_PER_FRAME) { playPop(); popsThisFrame++; }
            } else {
                stillPending.push(item);
            }
        }
        state.popQueue = stillPending;

        // ── Recolour queue (rainbow bubble staggered recolouring) ─────────────
        if (state.recolourQueue.length > 0) {
            const stillRecolouring = [];
            for (const item of state.recolourQueue) {
                if (timestamp >= item.fireAt) {
                    state.grid[item.r][item.c] = item.toColor;
                    playRainbowSfx();
                } else {
                    stillRecolouring.push(item);
                }
            }
            state.recolourQueue = stillRecolouring;
        }
        // When all recolours done, run findConnected from the original landing cell
        if (state.pendingConnect && state.recolourQueue.length === 0 && timestamp >= state.pendingConnect.after) {
            const { r, c } = state.pendingConnect;
            state.pendingConnect = null;
            tryPop(state, r, c, timestamp);
            checkDangerLine(state, timestamp);
        }

        // ── Bomb expiry check ─────────────────────────────────────────────────
        for (let r = 0; r < state.grid.length; r++) {
            for (let c = 0; c < (state.grid[r]?.length || 0); c++) {
                const cell = state.grid[r][c];
                if (!cell?.isBomb || timestamp < cell.expiresAt) continue;
                // Explode — orange burst then convert all neighbours to locked
                const bx = bubbleX(c, r, state.topParity);
                const by = bubbleY(r);
                state.particles.push(...makePopParticles(bx, by, { hex: '#ff5500' }));
                playPop();
                let lockedAny = false;
                for (const [nr, nc] of getNeighbours(r, c, state.grid, state.topParity)) {
                    const nb = state.grid[nr]?.[nc];
                    if (nb && nb.id !== 'locked') { state.grid[nr][nc] = LOCKED_COLOR; lockedAny = true; }
                }
                if (lockedAny) playLockSfx();
                state.grid[r][c] = LOCKED_COLOR;
                checkDangerLine(state, timestamp);
            }
        }

        // ── Board-clear check (runs after queue drains, not inside snapAndPop) ─
        if (!state.cascading && !state.clearedDisplay && state.pendingClearRows === 0
            && state.popQueue.length === 0 && !state.projectile) {
            const hasNonLocked = state.grid.some(row => row?.some(b => b && b.id !== 'locked'));
            if (!hasNonLocked) {
                state.score += 500;
                setScore(state.score);
                state.clearedDisplay   = { life: 1.0 };
                state.pendingClearRows = 5;
                state.nextClearRowAt   = timestamp + 500;
                playClearedSfx();
            }
        }

        // ── Cascade pop queue ─────────────────────────────────────────────────
        if (state.cascading) {
            const stillCascading = [];
            for (const item of state.cascadeQueue) {
                if (timestamp >= item.fireAt) {
                    state.particles.push(...makePopParticles(item.x, item.y, item.color));
                    if (popsThisFrame < MAX_POPS_PER_FRAME) { playPop(); popsThisFrame++; }
                }
                else stillCascading.push(item);
            }
            state.cascadeQueue = stillCascading;

            if (state.cascadeQueue.length === 0 && state.particles.length === 0) {
                state.phase = 'gameover';
                setPhase('gameover');
                fadeMusicOut(1800);
            }
        }

        // ── Particles — update & compact in-place (no filter allocation) ──────
        const particles = state.particles;
        let pCount = 0;
        for (let i = 0; i < particles.length; i++) {
            const p = particles[i];
            p.x += p.vx; p.y += p.vy; p.vy += 0.11; p.life -= p.decay;
            if (p.life > 0) particles[pCount++] = p;
        }
        particles.length = pCount;

        // ── Multi-display decay ───────────────────────────────────────────────
        if (state.multiDisplay) {
            state.multiDisplay.life -= 0.012;
            if (state.multiDisplay.life <= 0) state.multiDisplay = null;
        }
        if (state.clearedDisplay) {
            state.clearedDisplay.life -= 0.010; // slightly slower — ~1.7s
            if (state.clearedDisplay.life <= 0) state.clearedDisplay = null;
        }
        if (state.timerAlert) {
            state.timerAlert.life -= 0.014; // ~1.2s total at 60fps
            if (state.timerAlert.life <= 0) state.timerAlert = null;
        }
        // Score floats — rise upward and fade
        const sFloats = state.scoreFloats;
        let sfCount = 0;
        for (let i = 0; i < sFloats.length; i++) {
            const f = sFloats[i];
            f.y   -= 0.8;
            f.life -= 0.030;
            if (f.life > 0) sFloats[sfCount++] = f;
        }
        sFloats.length = sfCount;

        // ── Draw ──────────────────────────────────────────────────────────────
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        const isPlayingPhase = state.phase === 'playing' || state.phase === 'cascading' || state.phase === 'gameover' || state.phase === 'win';
        if (!isPlayingPhase) {
            ctx.fillStyle = '#0d0b1a';
            ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
            // Draw pre-built scanlines offscreen canvas — single drawImage vs N fillRects
            ctx.drawImage(getScanlines(), 0, 0);
        }

        if (state.phase === 'playing') {
            const handImg = handImgRef.current;
            if (handImg) {
                const hw = handImg.naturalWidth;
                const hh = handImg.naturalHeight;
                ctx.save();
                ctx.translate(SHOOTER_X, SHOOTER_Y + 8);
                ctx.rotate(state.angle + Math.PI / 2);
                ctx.drawImage(handImg, -hw / 2, -hh / 2, hw, hh);
                ctx.restore();

                ctx.save();
                ctx.globalCompositeOperation = 'source-atop';
                ctx.fillStyle = 'rgba(0,15,20,0.40)';
                ctx.fillRect(0, DANGER_Y, CANVAS_W, CANVAS_H - DANGER_Y);
                ctx.restore();
            }
        }

        // Aim line
        if (!state.projectile && state.phase === 'playing') {
            ctx.strokeStyle = 'rgba(255,255,255,0.55)';
            ctx.setLineDash([4, 7]); ctx.lineWidth = 2;
            ctx.beginPath();
            state.aimLine.forEach((pt, i) => i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y));
            ctx.stroke();
            ctx.setLineDash([]);
            const last = state.aimLine[state.aimLine.length - 1];
            ctx.fillStyle = 'rgba(255,255,255,0.75)';
            ctx.beginPath(); ctx.arc(last.x, last.y, 4, 0, Math.PI * 2); ctx.fill();
        }

        // Grid bubbles — rows are top→bottom so break as soon as we leave the canvas
        for (let r = 0; r < state.grid.length; r++) {
            const by = bubbleY(r);
            if (by > CANVAS_H + BUBBLE_SIZE) break;
            for (let c = 0; c < (state.grid[r]?.length || 0); c++) {
                const cell = state.grid[r][c];
                if (!cell) continue;
                const bx = bubbleX(c, r, state.topParity);
                if (cell.id === 'rainbow') {
                    drawRainbowBubble(ctx, img, bx, by, BUBBLE_SIZE, timestamp);
                } else if (cell.isFrankly) {
                    drawFranklyBubble(ctx, img, franklyStampImgRef.current, bx, by, BUBBLE_SIZE);
                } else if (cell.isBomb) {
                    const secsLeft = Math.max(0, Math.ceil((cell.expiresAt - timestamp) / 1000));
                    drawBombBubble(ctx, img, bx, by, BUBBLE_SIZE, secsLeft, timestamp);
                } else if (cell.id === 'locked') {
                    ctx.drawImage(getBlackSilhouette(img, BUBBLE_SIZE), bx - BUBBLE_SIZE / 2, by - BUBBLE_SIZE / 2, BUBBLE_SIZE, BUBBLE_SIZE);
                    drawLockIcon(ctx, bx, by, BUBBLE_SIZE, padlockImgRef.current);
                } else {
                    drawBubble(ctx, img, bx, by, cell, BUBBLE_SIZE);
                }
            }
        }

        // Pending pop queue (still visible until their particle fires)
        for (const item of state.popQueue)
            drawBubble(ctx, img, item.x, item.y, item.color, BUBBLE_SIZE);

        // Cascade queue (still visible until their particle fires)
        for (const item of state.cascadeQueue)
            drawBubble(ctx, img, item.x, item.y, item.color, BUBBLE_SIZE);

        // Rising locked / frankly bubbles
        for (const lb of state.risingLocked) {
            if (lb.isFrankly) {
                drawFranklyBubble(ctx, img, franklyStampImgRef.current, lb.x, lb.y, BUBBLE_SIZE);
            } else {
                ctx.drawImage(getBlackSilhouette(img, BUBBLE_SIZE), lb.x - BUBBLE_SIZE / 2, lb.y - BUBBLE_SIZE / 2, BUBBLE_SIZE, BUBBLE_SIZE);
                drawLockIcon(ctx, lb.x, lb.y, BUBBLE_SIZE, padlockImgRef.current);
            }
        }

        // Cannon bubble and Next-up queue
        if (state.phase === 'playing') {
            // Current bubble on cannon
            if (!state.projectile && state.shootColor) {
                if (state.shootColor.id === 'bomb') {
                    if (state.bombCannonTimer) {
                        // Show live countdown while fuse is burning
                        const fuseLeft = Math.max(0, Math.ceil(
                            (BOMB_CANNON_FUSE_MS - (timestamp - state.bombCannonTimer.startedAt)) / 1000
                        ));
                        drawBombBubble(ctx, img, SHOOTER_X, SHOOTER_Y, BUBBLE_SIZE, fuseLeft, timestamp);
                    } else {
                        drawBombQueued(ctx, img, SHOOTER_X, SHOOTER_Y, BUBBLE_SIZE, timestamp);
                    }
                } else {
                    //drawCannonOutline(ctx, img, SHOOTER_X, SHOOTER_Y, state.shootColor, BUBBLE_SIZE);
                    drawBubble(ctx, img, SHOOTER_X, SHOOTER_Y, state.shootColor, BUBBLE_SIZE);
                }
            }
        }

        // Projectile in flight
        if (state.projectile) {
            if (state.projectile.color.id === 'bomb') {
                drawBombQueued(ctx, img, state.projectile.x, state.projectile.y, BUBBLE_SIZE, timestamp);
            } else {
                drawBubble(ctx, img, state.projectile.x, state.projectile.y, state.projectile.color, BUBBLE_SIZE);
            }
        }

        // Next-up queue (3 × small bubbles above-left of cannon; rightmost = next to fire)
        if (state.phase === 'playing') {
            const SMALL = 21;
            const qStartX = SHOOTER_X - 84;
            const qY = SHOOTER_Y - 10;
            [state.nextNextNext, state.nextNext, state.nextColor].forEach((col, i) => {
                if (!col) return;
                if (col.id === 'bomb') drawBombQueued(ctx, img, qStartX + i * (SMALL + 4), qY, SMALL, timestamp);
                else drawBubble(ctx, img, qStartX + i * (SMALL + 4), qY, col, SMALL);
            });
        }

        // Timer
        if (state.phase === 'playing' || state.phase === 'cascading') {
            const t = state.timeLeft;
            ctx.font = "18px 'Jersey 10', monospace";
            ctx.textAlign = 'center';
            ctx.fillStyle = timerColor(t);
            if (t <= 10) {
                ctx.globalAlpha = 0.7 + 0.3 * Math.sin(timestamp / 150);
            }
            ctx.fillText(formatTime(t), CANVAS_W - 30, DANGER_Y + 100);
            ctx.globalAlpha = 1;
            ctx.textAlign = 'left';
        }

        // Particles
        for (let i = 0; i < particles.length; i++) {
            const p = particles[i];
            ctx.globalAlpha = Math.max(0, p.life * 0.9);
            ctx.fillStyle = p.color;
            ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Score floats — small +pts text rising from each popped bubble
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (let i = 0; i < sFloats.length; i++) {
            const f = sFloats[i];
            ctx.globalAlpha = Math.min(1, f.life * 2.5); // fast fade-in, slow fade-out
            ctx.font = "bold 13px 'Jersey 10', monospace";
            ctx.fillStyle = f.pts >= 15 ? '#ffdd44' : '#ffffff';
            ctx.shadowColor = f.pts >= 15 ? '#ff8800' : 'rgba(0,0,0,0.6)';
            ctx.shadowBlur = f.pts >= 15 ? 6 : 3;
            ctx.fillText(`+${f.pts}`, f.x, f.y);
        }
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';

        // Timer alert flash (30s, 20s, 10-1 countdown)
        if (state.timerAlert) {
            const { text, life, color } = state.timerAlert;
            let scale, alpha;
            if (life > 0.75) {
                const t = (1.0 - life) / 0.25;
                scale = 0.5 + t * 0.65;
                alpha = t;
            } else if (life > 0.35) {
                scale = 1.15;
                alpha = 1.0;
            } else {
                const t = life / 0.35;
                scale = 1.15;
                alpha = t * 0.85;
            }
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.translate(CANVAS_W / 2, CANVAS_H / 2);
            ctx.scale(scale, scale);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.shadowColor = color;
            ctx.shadowBlur = 20;
            ctx.font = `bold ${text.length <= 2 ? 96 : 52}px 'Jersey 10', monospace`;
            ctx.fillStyle = '#ffffff';
            ctx.fillText(text, 0, 0);
            ctx.shadowBlur = 0;
            ctx.strokeStyle = color;
            ctx.lineWidth = 2 / scale;
            ctx.strokeText(text, 0, 0);
            ctx.fillText(text, 0, 0);
            ctx.restore();
        }

        // MULTI pop graphic
        if (state.multiDisplay) {
            const { count, life } = state.multiDisplay;
            let scale, alpha;
            if (life > 0.75) {
                const t = (1.0 - life) / 0.25;
                scale = 0.4 + t * 0.9;
                alpha = t;
            } else if (life > 0.3) {
                scale = 1.3;
                alpha = 1.0;
            } else {
                const t = life / 0.3;
                scale = 1.3 - (1 - t) * 0.15;
                alpha = t;
            }
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.translate(CANVAS_W / 2, CANVAS_H / 2 - 50);
            ctx.scale(scale, scale);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.shadowColor = '#ff7700';
            ctx.shadowBlur = 22;
            ctx.font = "bold 46px 'Jersey 10', monospace";
            ctx.fillStyle = '#ffffff';
            ctx.fillText(`MULTI ${count}`, 0, 0);
            ctx.shadowBlur = 0;
            ctx.strokeStyle = 'rgba(255,120,0,0.9)';
            ctx.lineWidth = 2.5 / scale;
            ctx.strokeText(`MULTI ${count}`, 0, 0);
            ctx.fillText(`MULTI ${count}`, 0, 0);
            ctx.restore();
        }

        // CLEARED bonus graphic
        if (state.clearedDisplay) {
            const { life } = state.clearedDisplay;
            let scale, alpha;
            if (life > 0.75) {
                const t = (1.0 - life) / 0.25;
                scale = 0.3 + t * 1.0;
                alpha = t;
            } else if (life > 0.25) {
                scale = 1.3;
                alpha = 1.0;
            } else {
                const t = life / 0.25;
                scale = 1.3;
                alpha = t;
            }
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.translate(CANVAS_W / 2, CANVAS_H / 2 - 20);
            ctx.scale(scale, scale);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.shadowColor = '#00e5ff';
            ctx.shadowBlur = 28;
            ctx.font = "bold 52px 'Jersey 10', monospace";
            ctx.fillStyle = '#ffffff';
            ctx.fillText('CLEARED!', 0, 0);
            ctx.shadowBlur = 0;
            ctx.strokeStyle = 'rgba(0,200,255,0.9)';
            ctx.lineWidth = 2.5 / scale;
            ctx.strokeText('CLEARED!', 0, 0);
            ctx.fillText('CLEARED!', 0, 0);
            // +500 pts sub-text
            ctx.font = "bold 24px 'Jersey 10', monospace";
            ctx.fillStyle = 'rgba(0,230,255,0.9)';
            ctx.fillText('+500', 0, 44);
            ctx.restore();
        }

        // FREEZE overlay — bomb cannon expired, controls locked
        // Game continues running; only the text is drawn over the live frame
        if (state.frozen && state.frozenUntil) {
            const secsLeft = Math.max(0, Math.ceil((state.frozenUntil - timestamp) / 1000));
            ctx.save();
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.font = `bold 110px 'Jersey 10', monospace`;
            ctx.shadowColor = 'rgba(0,0,0,0.9)';
            ctx.shadowBlur = 24;
            ctx.fillStyle = '#ffffff';
            ctx.fillText(String(secsLeft), CANVAS_W / 2, CANVAS_H / 2 - 18);
            ctx.shadowBlur = 0;
            ctx.strokeStyle = 'rgba(255,255,255,0.55)';
            ctx.lineWidth = 2;
            ctx.strokeText(String(secsLeft), CANVAS_W / 2, CANVAS_H / 2 - 18);
            ctx.fillText(String(secsLeft), CANVAS_W / 2, CANVAS_H / 2 - 18);
            ctx.font = `bold 18px 'Jersey 10', monospace`;
            ctx.shadowColor = 'rgba(0,0,0,0.85)';
            ctx.shadowBlur = 8;
            ctx.fillStyle = 'rgba(255,255,255,0.75)';
            ctx.fillText('CONTROLS FROZEN', CANVAS_W / 2, CANVAS_H / 2 + 68);
            ctx.shadowBlur = 0;
            ctx.restore();
        }

        const keepLooping = state.phase === 'playing' || state.phase === 'cascading';
        if (keepLooping) rafRef.current = requestAnimationFrame(gameLoop);
    }, []);

    // ── Loop lifecycle ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (phase === 'playing') {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(gameLoop);
        }
        return () => cancelAnimationFrame(rafRef.current);
    }, [phase, gameLoop]);

    // ── Page Visibility — pause loops when tab is hidden, resume cleanly ──────
    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                cancelAnimationFrame(rafRef.current);
            } else {
                // Reset timer anchor so hidden time doesn't count as elapsed game time
                const s = stateRef.current;
                if (s && (s.phase === 'playing' || s.phase === 'cascading')) {
                    s.lastTick = null;
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = requestAnimationFrame(gameLoop);
                }
                // Restart bg animation loop if it was running
                if (bgLoopRef.current) {
                    cancelAnimationFrame(bgRafRef.current);
                    bgRafRef.current = requestAnimationFrame(bgLoopRef.current);
                }
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [gameLoop]);

    // ── Avatar random head-turn ────────────────────────────────────────────────
    useEffect(() => {
        if (phase !== 'playing') {
            clearTimeout(avatarHeadTimerRef.current);
            setAvatarState(prev => ({ ...prev, headDir: 2 }));
            return;
        }
        const scheduleNextTurn = () => {
            // Random wait 4–12s before looking sideways
            const delay = 4000 + Math.random() * 8000;
            avatarHeadTimerRef.current = setTimeout(() => {
                setAvatarState(prev => ({ ...prev, headDir: 3 }));
                // Hold for 1.5–3s then look back
                avatarHeadTimerRef.current = setTimeout(() => {
                    setAvatarState(prev => ({ ...prev, headDir: 2 }));
                    scheduleNextTurn();
                }, 1500 + Math.random() * 1500);
            }, delay);
        };
        scheduleNextTurn();
        return () => clearTimeout(avatarHeadTimerRef.current);
    }, [phase]);

    // ── Cleanup on unmount (stop music, clear avatar timers, release caches) ──
    useEffect(() => {
        return () => {
            stopMusic();
            clearTimeout(avatarWaveTimerRef.current);
            clearTimeout(avatarHeadTimerRef.current);
            clearTimeout(avatarJumpTimerRef.current);
            // Release module-level offscreen canvas caches so memory is reclaimed
            // if the player navigates away and back.
            _tintCache.clear();
            _bombBaseCache.clear();
            _bombRedCache.clear();
            _franklyStampCache.clear();
            _franklySilCache.clear();
            _blackSilhouetteCache.clear();
            _scanlinesCanvas = null;
            _rainbowCanvas   = null;
        };
    }, []);

// ── Keyboard ──────────────────────────────────────────────────────────────
    useEffect(() => {
        const down = e => {
            if (stateRef.current?.phase === 'playing' && ['ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                e.preventDefault();
            }

            if (['ArrowLeft',  'a', 'A'].includes(e.key)) keysRef.current.left  = true;
            if (['ArrowRight', 'd', 'D'].includes(e.key)) keysRef.current.right = true;
            if (['w', 'W', ' '].includes(e.key)) keysRef.current.fire = true;
        };
        const up = e => {
            if (['ArrowLeft',  'a', 'A'].includes(e.key)) keysRef.current.left  = false;
            if (['ArrowRight', 'd', 'D'].includes(e.key)) keysRef.current.right = false;
            if (['w', 'W', ' '].includes(e.key)) keysRef.current.fire = false;
        };
        window.addEventListener('keydown', down, { passive: false });
        window.addEventListener('keyup',   up);
        return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
    }, []);

    // ── Return to start screen (from gameover / win) ──────────────────────────
    const goToStart = useCallback(() => {
        cancelAnimationFrame(rafRef.current);
        stateRef.current = null;
        setPhase('idle');
        setSubmitModalOpen(false);
        restartMusic();
    }, []);

    // ── Submit score modal confirm ─────────────────────────────────────────────
    const handleSubmit = useCallback(async () => {
        if (!pendingScoreId || submitting) return;
        setSubmitting(true);
        try {
            const data = await submitGameScore(pendingScoreId);
            if (data.success) {
                setSubmitModalOpen(false);
                setUserCanSubmit(false);
                setPendingScoreId(null);
                getLatestGameSubmissions().then(d => { if (d.success) setSubmissions(d.submissions); }).catch(() => {});
                getGameLeaderboard('winners').then(d => { if (d.success) setLeaderboardWin(d.leaderboard); }).catch(() => {});
                getGameLeaderboard('lose').then(d => { if (d.success) setLeaderboardLose(d.leaderboard); }).catch(() => {});
            }
        } catch {
            // silent — server-side errors are visible in network tab
        } finally {
            setSubmitting(false);
        }
    }, [pendingScoreId, submitting]);

    // ── Audio controls ────────────────────────────────────────────────────────
    const handleSfxVol = useCallback((v) => {
        const n = parseFloat(v);
        setSfxVolState(n);
        setSfxVol(n);
        localStorage.setItem('lobbyburst_sfx', String(n));
    }, []);

    const handleMusicVol = useCallback((v) => {
        const n = parseFloat(v);
        // Clear any in-progress fade so the manual adjustment isn't overridden mid-interval
        if (_fadeInterval) { clearInterval(_fadeInterval); _fadeInterval = null; }
        _musicVol = n;
        if (_isMobile) {
            // iOS does not support JS volume control — use pause/play instead
            if (n === 0) { _music.pause(); }
            else if (_music.paused) startMusic();
        } else {
            _music.volume = n;
            if (n > 0 && _music.paused) startMusic();
        }
        setMusicVolState(n);
        localStorage.setItem('lobbyburst_music', String(n));
    }, []);

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="mt-10">
            <div className="flex flex-col xl:flex-row gap-6 xl:items-start">

                {/* ── Left column: game ── */}
                <div
                    className="flex-1 min-w-0 tracking-widest"
                    onContextMenu={e => e.preventDefault()}
                    onSelectStart={e => e.preventDefault()}
                    style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', WebkitTouchCallout: 'none' }}
                >
                    <ContentBox header="Lobby Burst!" theme="battleball" tint="#000F14" icon="/img/icons/icon_202.png">
                        {/* Outer game container  */}
                        <div
                            className="flex flex-col lg:flex-row rounded max-w-[1050px] mx-auto"
                            style={{ background: '#000F14', border: '1px solid rgba(255,255,255,0.07)', boxShadow: 'inset 0 0 60px rgba(0,0,0,0.5)', fontFamily: "'Jersey 10', monospace" }}
                        >
                            {/* ── Sidebar ── */}
                            <div className="lg:w-52 flex-shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-white/[0.06]">

                                {/* Mobile: compact horizontal strip */}
                                <div className="flex lg:hidden flex-row items-center justify-between px-4 py-2 gap-6 border-b border-white/[0.06]">
                                    <div>
                                        <div className="text-white/30 text-xs uppercase tracking-[2px]">Score</div>
                                        <div className="text-white text-lg leading-none tabular-nums">{score.toString().padStart(6, '0')}</div>
                                    </div>
                                    <div>
                                        <div className="text-white/30 text-xs uppercase tracking-[2px]">Time</div>
                                        <div className="text-lg leading-none tabular-nums"
                                             style={{ color: timeLeft > 60 ? 'white' : timeLeft > 30 ? '#ffc840' : '#e03030' }}>
                                            {formatTime(timeLeft)}
                                        </div>
                                    </div>
                                </div>

                                {/* Desktop: full sidebar */}
                                <div className="hidden lg:flex flex-col flex-1">
                                    <div className="px-4 py-4 border-b border-white/[0.06]">
                                        <div className="text-white/30 text-lg uppercase tracking-[3px] mb-1">Score</div>
                                        <div className="text-white text-[28px] leading-none tabular-nums"
                                             style={{ textShadow: '0 0 14px rgba(120,80,220,0.35)' }}>
                                            {score.toString().padStart(6, '0')}
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 border-b border-white/[0.06]">
                                        <div className="text-white/30 text-lg uppercase tracking-[2px] mb-1">Time</div>
                                        <div className="text-[28px] leading-none tabular-nums"
                                             style={{
                                                 color: timeLeft > 60 ? 'white' : timeLeft > 30 ? '#ffc840' : '#e03030',
                                                 textShadow: timeLeft <= 30 ? '0 0 12px rgba(220,60,60,0.45)' : 'none'
                                             }}>
                                            {formatTime(timeLeft)}
                                        </div>
                                    </div>
                                    <div className="px-4 py-3 border-b border-white/[0.06]">
                                        <div className="text-white/30 text-lg uppercase tracking-[2px] mb-2">Controls</div>
                                        <div className="space-y-2">
                                            {[['A / ←', 'Left'], ['D / →', 'Right'], ['W / Spacebar', 'Fire']].map(([k, v]) => (
                                                <div key={k} className="flex justify-between items-baseline">
                                                    <span className="text-white/55 text-lg">{k}</span>
                                                    <span className="text-white/30 text-lg">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="pl-4 py-3 flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="text-white/30 text-lg uppercase tracking-[2px]">Top Scores</div>
                                            <div className="flex rounded overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                                                {['winners', 'lose'].map(mode => (
                                                    <button
                                                        key={mode}
                                                        onClick={() => setLbMode(mode)}
                                                        className="px-2 py-0.5 text-sm uppercase tracking-[1px] transition-colors"
                                                        style={{
                                                            background: lbMode === mode ? 'rgba(255,255,255,0.12)' : 'transparent',
                                                            color: lbMode === mode ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)',
                                                            borderRight: mode === 'winners' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                                        }}
                                                    >
                                                        {mode === 'winners' ? 'Won' : 'Lost'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ overflowY: 'auto', height: 220 }}>
                                            {Array.from({ length: 20 }).map((_, i) => {
                                                const entry = (lbMode === 'winners' ? leaderboardWin : leaderboardLose)[i];
                                                return entry ? (
                                                    <div key={`${entry.habboName}-${i}`} className="flex items-center justify-between py-1.5 border-b border-white/[0.04] last:border-0 gap-1 mr-4">
                                                        <span className="text-white/55 text-lg truncate" style={{ maxWidth: 108 }}>{i + 1}. {entry.habboName}</span>
                                                        <span className="text-white/40 text-lg tabular-nums flex-shrink-0">{entry.score.toString().padStart(6, '0')}</span>
                                                    </div>
                                                ) : (
                                                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/[0.04] last:border-0 mr-4">
                                                        <span className="text-white/20 text-lg">{i + 1}. —</span>
                                                        <span className="text-white/15 text-lg">—</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Game area ── */}
                            <style>{`@keyframes habboJump{0%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}`}</style>
                            <div
                                className="relative overflow-hidden flex-1 aspect-[336/500] lg:aspect-[860/500]"
                                style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'none' }}
                            >
                                {/* Background image */}
                                {[BG_LOBBY_SRC, BG_CLOSE_SRC].map((src, i) => {
                                    const isClose = i === 1;
                                    const active = isClose
                                        ? (phase === 'playing' || phase === 'cascading')
                                        : !(phase === 'playing' || phase === 'cascading');
                                    return (
                                        <img
                                            key={src}
                                            src={src}
                                            alt=""
                                            draggable={false}
                                            style={{
                                                position: 'absolute', inset: 0,
                                                width: '100%', height: '100%',
                                                objectFit: 'cover',
                                                imageRendering: 'pixelated',
                                                pointerEvents: 'none', userSelect: 'none',
                                                zIndex: 0,
                                                opacity: active ? 1 : 0,
                                                transition: 'opacity 0.25s ease',
                                            }}
                                        />
                                    );
                                })}

                                {/* Current user's avatar */}
                                {assetsReady && (() => {
                                    const isVisible = phase === 'playing' || phase === 'cascading';
                                    const activeIdx = (avatarState.gesture === 'sml' ? 2 : 0) + (avatarState.headDir === 3 ? 1 : 0);
                                    const urls = avatarUrlsRef.current;
                                    return (
                                        <div style={{
                                            position: 'absolute',
                                            left: '40%',
                                            top: '53%',
                                            transform: 'translate(-50%, -50%) scale(8)',
                                            transformOrigin: 'center center',
                                            pointerEvents: 'none',
                                            userSelect: 'none',
                                            opacity: isVisible ? 0.6 : 0,
                                            zIndex: 1,
                                        }}>
                                            <div
                                                key={jumpKey}
                                                style={{
                                                    display: 'block',
                                                    animation: avatarJumping ? 'habboJump 0.32s ease-in-out 3' : 'none',
                                                }}
                                            >
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    {urls.map((src, i) => (
                                                        <img
                                                            key={i}
                                                            src={src}
                                                            alt=""
                                                            draggable={false}
                                                            style={{
                                                                display: 'block',
                                                                imageRendering: 'pixelated',
                                                                position: i === 0 ? 'relative' : 'absolute',
                                                                top: 0, left: 0,
                                                                opacity: i === activeIdx ? 1 : 0,
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Background bubble animation canvas */}
                                <canvas
                                    ref={bgCanvasRef}
                                    style={{
                                        position: 'absolute', top: 0, left: 0,
                                        width: '100%', height: '100%',
                                        imageRendering: 'pixelated',
                                        pointerEvents: 'none',
                                        zIndex: 2,
                                    }}
                                />

                                {/* Game flex column */}
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', zIndex: 4 }}>

                                    {/* Canvas area  */}
                                    <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
                                        <div style={{
                                            position: 'absolute',
                                            top: 0, left: '50%',
                                            transform: 'translateX(-50%)',
                                            height: '100%',
                                            aspectRatio: `${CANVAS_W} / ${CANVAS_H}`
                                        }}>
                                            {phase === 'playing' && (
                                                <>
                                                    <style>{`
                                                        @keyframes dangerPulse {
                                                            0%, 100% { border-top-color: #cccccc; }
                                                            50% { border-top-color: #ffffff; }
                                                        }
                                                    `}</style>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: `${((DANGER_Y / CANVAS_H) * 100) + 3}%`,
                                                        bottom: 0,
                                                        left: '-30px',
                                                        right: '-30px',
                                                        background: 'rgba(0,15,20,0.40)',
                                                        borderTop: '2px dashed #cccccc',
                                                        animation: 'dangerPulse 1.5s infinite',
                                                        pointerEvents: 'none',
                                                        zIndex: -1,
                                                    }} />
                                                </>
                                            )}
                                            <canvas
                                                ref={canvasRef}
                                                width={CANVAS_W}
                                                height={CANVAS_H}
                                                style={{
                                                    width: '100%', height: '100%',
                                                    imageRendering: 'pixelated',
                                                    cursor: 'default',
                                                    position: 'relative',
                                                    zIndex: 1
                                                }}
                                            />
                                        </div>

                                        {/* Audio control panel — bottom-right of canvas area */}
                                        {_isMobile ? (
                                            // Mobile: simple music on/off toggle (iOS doesn't support JS volume control)
                                            <button
                                                onClick={() => handleMusicVol(musicVol > 0 ? '0' : '0.75')}
                                                style={{
                                                    position: 'absolute', bottom: 10, right: 10, zIndex: 10,
                                                    background: musicVol > 0 ? 'rgba(123,79,255,0.25)' : 'rgba(0,0,0,0.45)',
                                                    border: `1px solid ${musicVol > 0 ? 'rgba(123,79,255,0.5)' : 'rgba(255,255,255,0.15)'}`,
                                                    borderRadius: 6, padding: '5px 6px',
                                                    cursor: 'pointer', lineHeight: 0,
                                                    backdropFilter: 'blur(4px)',
                                                }}
                                                title={musicVol > 0 ? 'Music off' : 'Music on'}
                                            >
                                                {musicVol === 0 ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                        <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                                                    </svg>
                                                )}
                                            </button>
                                        ) : (
                                            // Desktop: full slider panel
                                            <div
                                                ref={audioMenuRef}
                                                style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                                {/* Expanded sliders */}
                                                {audioMenuOpen && (
                                                    <div style={{
                                                        background: 'rgba(8,6,20,0.88)',
                                                        border: '1px solid rgba(255,255,255,0.12)',
                                                        borderRadius: 8,
                                                        padding: '10px 12px',
                                                        backdropFilter: 'blur(8px)',
                                                        minWidth: 150,
                                                        maxWidth: 'calc(100vw - 32px)',
                                                        fontFamily: "'Jersey 10', monospace",
                                                        display: 'flex', flexDirection: 'column', gap: 10,
                                                    }}>
                                                        {[
                                                            { label: 'Sound FX', val: sfxVol,   onChange: handleSfxVol },
                                                            { label: 'Music',    val: musicVol, onChange: handleMusicVol },
                                                        ].map(({ label, val, onChange }) => (
                                                            <div key={label}>
                                                                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <input
                                                                        type="range" min="0" max="1" step="0.05"
                                                                        value={val}
                                                                        onChange={e => onChange(e.target.value)}
                                                                        style={{ flex: 1, accentColor: '#7b4fff', cursor: 'pointer', height: 3, minWidth: 90 }}
                                                                    />
                                                                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, minWidth: 26, textAlign: 'right' }}>
                                                                        {Math.round(val * 100)}%
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {/* Toggle button */}
                                                <button
                                                    onClick={() => setAudioMenuOpen(o => !o)}
                                                    style={{
                                                        background: audioMenuOpen ? 'rgba(123,79,255,0.25)' : 'rgba(0,0,0,0.45)',
                                                        border: `1px solid ${audioMenuOpen ? 'rgba(123,79,255,0.5)' : 'rgba(255,255,255,0.15)'}`,
                                                        borderRadius: 6, padding: '5px 6px',
                                                        cursor: 'pointer', lineHeight: 0,
                                                        backdropFilter: 'blur(4px)',
                                                    }}
                                                    title="Audio settings"
                                                >
                                                    {(sfxVol === 0 && musicVol === 0) ? (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                            <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
                                                        </svg>
                                                    ) : (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {/* Loading screen */}
                                        {!assetsReady && (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center"
                                                 style={{ background: 'rgba(8,6,18,0.95)', zIndex: 10 }}>
                                                <div className="text-white/50 text-xl uppercase tracking-[4px] mb-5">Loading</div>
                                                <div className="flex gap-2">
                                                    {BUBBLE_COLORS.map((c, i) => (
                                                        <div key={c.id} className="w-3 h-3 rounded-full"
                                                             style={{
                                                                 background: c.hex,
                                                                 opacity: 0.4,
                                                                 animation: `pulse 1.2s ease-in-out ${i * 0.15}s infinite`,
                                                             }} />
                                                    ))}
                                                </div>
                                                <style>{`@keyframes pulse{0%,100%{opacity:.25}50%{opacity:1}}`}</style>
                                            </div>
                                        )}

                                        {/* Overlays */}
                                        {assetsReady && (phase === 'idle' || phase === 'gameover' || phase === 'win') && (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center"
                                                 style={{ background: 'rgba(8,6,18,0.72)', backdropFilter: 'blur(3px)' }}>

                                                {phase === 'idle' && (
                                                    <>
                                                        <div className="text-center mb-7 px-6">
                                                            <div className="text-lg text-white/25 uppercase tracking-[4px] -mb-2">Frankly Studios</div>
                                                            <div className="text-white text-[44px] uppercase leading-none"
                                                                 style={{ textShadow: '0 0 40px rgba(140,100,255,0.45), 0 2px 0 rgba(0,0,0,0.8)' }}>
                                                                Lobby Burst!
                                                            </div>
                                                            <div className="text-white/80 text-xl mt-3 leading-relaxed">
                                                                Match &amp; pop 3 or more bubbles<br />
                                                                <span className="text-white/70">Survive 3 minutes to win</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 mb-8">
                                                            {BUBBLE_COLORS.map(c => (
                                                                <div key={c.id} className="w-5 h-5 rounded-full"
                                                                     style={{ background: c.hex, boxShadow: `0 0 6px ${c.hex}55` }} />
                                                            ))}
                                                        </div>
                                                        <button onClick={startGame}
                                                                className="px-10 py-3 rounded text-[22px] uppercase tracking-[3px] transition-all hover:scale-105 active:scale-95"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #8a1a1a 0%, #c02828 100%)',
                                                                    boxShadow: '0 4px 24px rgba(160,30,30,0.45), inset 0 1px 0 rgba(255,255,255,0.1)',
                                                                    color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                                                                    textShadow: '0 1px 4px rgba(0,0,0,0.5)'
                                                                }}>
                                                            Play!
                                                        </button>
                                                        <div className="text-white/25 text-lg uppercase tracking-[2px] mt-4">
                                                            Keyboard · Touch
                                                        </div>
                                                    </>
                                                )}

                                                {phase === 'gameover' && (
                                                    <>
                                                        <div className="text-[36px] text-red-400 uppercase mb-2"
                                                             style={{ textShadow: '0 0 24px rgba(200,50,50,0.5)' }}>Game Over</div>
                                                        <div className="text-white/35 text-xl mb-1">Final Score</div>
                                                        <div className="text-white text-[34px] mb-8 tabular-nums">{score.toString().padStart(6, '0')}</div>
                                                        <button onClick={goToStart}
                                                                className="px-8 py-2.5 rounded text-[20px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #8a1a1a 0%, #c02828 100%)',
                                                                    boxShadow: '0 4px 20px rgba(160,30,30,0.4)',
                                                                    color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                                                                }}>
                                                            Play Again
                                                        </button>
                                                        {!userData ? (
                                                            <div className="mt-3 text-white/40 text-[13px] uppercase tracking-[2px]">Login to Submit Score!</div>
                                                        ) : (userCanSubmit && pendingScoreId && (
                                                            <button
                                                                onClick={() => setSubmitModalOpen(true)}
                                                                className="mt-3 px-8 py-2 rounded text-[16px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
                                                                    boxShadow: '0 4px 18px rgba(100,50,220,0.35)',
                                                                    color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                                                                }}>
                                                                Submit Score
                                                            </button>
                                                        ))}
                                                    </>
                                                )}

                                                {phase === 'win' && (
                                                    <>
                                                        <div className="text-[38px] text-yellow-300 uppercase mb-2"
                                                             style={{ textShadow: '0 0 30px rgba(240,210,0,0.55)' }}>You Win!</div>
                                                        <div className="text-white/35 text-xl mb-1">Final Score</div>
                                                        <div className="text-white text-[34px] mb-8 tabular-nums">{score.toString().padStart(6, '0')}</div>
                                                        <button onClick={goToStart}
                                                                className="px-8 py-2.5 rounded text-[20px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #8a1a1a 0%, #c02828 100%)',
                                                                    boxShadow: '0 4px 20px rgba(160,30,30,0.4)',
                                                                    color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                                                                }}>
                                                            Play Again
                                                        </button>
                                                        {!userData ? (
                                                            <div className="mt-3 text-white/40 text-[13px] uppercase tracking-[2px]">Login to Submit Score!</div>
                                                        ) : (userCanSubmit && pendingScoreId && (
                                                            <button
                                                                onClick={() => setSubmitModalOpen(true)}
                                                                className="mt-3 px-8 py-2 rounded text-[16px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
                                                                    boxShadow: '0 4px 18px rgba(100,50,220,0.35)',
                                                                    color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                                                                }}>
                                                                Submit Score
                                                            </button>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Mobile controls — pinned at bottom of game area */}
                                    {phase === 'playing' && (
                                        <div className="lg:hidden flex items-center justify-between px-2 py-2 gap-2 flex-shrink-0"
                                             style={{ background: 'rgba(0,0,0,0.5)' }}>
                                            <button
                                                className="flex-1 py-3 rounded text-white text-xl uppercase tracking-widest select-none"
                                                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', touchAction: 'none' }}
                                                onTouchStart={e => { e.preventDefault(); keysRef.current.left = true; }}
                                                onTouchEnd={e => { e.preventDefault(); keysRef.current.left = false; }}
                                            >◀</button>
                                            <button
                                                className="flex-1 py-3 rounded text-white text-xl uppercase tracking-widest select-none"
                                                style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', touchAction: 'none' }}
                                                onTouchStart={e => { e.preventDefault(); const s = stateRef.current; if (s) doFire(s); }}
                                                onTouchEnd={e => e.preventDefault()}
                                            >Fire</button>
                                            <button
                                                className="flex-1 py-3 rounded text-white text-xl uppercase tracking-widest select-none"
                                                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', touchAction: 'none' }}
                                                onTouchStart={e => { e.preventDefault(); keysRef.current.right = true; }}
                                                onTouchEnd={e => { e.preventDefault(); keysRef.current.right = false; }}
                                            >▶</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </ContentBox>

                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="flex-1">
                            <ContentBox header="How to Play" theme="battleball" tint="green">
                                <div className="space-y-5 p-1">

                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Objective</div>
                                        <div className="text-coal text-sm">
                                            Shoot coloured bubbles at the cluster above. Match 3 or more of the same colour to pop them. Survive 3 minutes to win.
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-2">Controls</div>
                                        <div className="space-y-1.5">
                                            {[
                                                ['A / ←', 'Aim left'],
                                                ['D / →', 'Aim right'],
                                                ['W / Space', 'Fire'],
                                                ['Touch', 'Buttons below game'],
                                            ].map(([k, v]) => (
                                                <div key={k} className="flex justify-between border-b border-black/10 pb-1 items-baseline gap-4">
                                                    <span className="text-coal text-sm">{k}</span>
                                                    <span className="text-coal/60 text-sm">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Scoring</div>
                                        <div className="text-coal text-sm leading-relaxed space-y-1">
                                            <div>+10 pts per bubble popped</div>
                                            <div className="text-coal/50">+15 bonus per extra bubble beyond 3</div>
                                            <div className="text-coal/50">Floating bubbles drop and score too</div>
                                        </div>
                                    </div>
                                </div>
                            </ContentBox>
                        </div>
                        <div className="flex-1 min-w-0">
                            <ContentBox header="Earn Framps!" theme="battleball" tint="maroon">
                                <div className="p-1">

                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Earn Daily Framps</div>
                                        <div className="text-coal text-sm leading-relaxed">
                                            You can earn framps by playing this game each day. When you finish a game, either by winning or game over, you can choose to submit your score. You can only submit a score once per day, it is your choice whether to submit a losing score for less framps, or attempt to submit a winning score. Your framp reward will then be calculated as followed:
                                        </div>
                                        <div className="mt-2 ml-2">
                                            <b className="text-coal/75 text-sm uppercase tracking-[2px] mb-1">Gameover?</b><br />
                                            <div className="text-sm">Submit your score and be instantly rewarded with 1-3 framps.</div>
                                        </div>
                                        <div className="mt-2">
                                            <b className="text-coal/75 text-sm uppercase tracking-[2px] mb-1 ml-2">Winner?</b><br />
                                            <div className="text-sm ml-2">Submit your score and be instantly rewarded with your score divided by 100 in framps (rounded up). I.e., if you scored 3000 points, you would be rewarded with 30 framps, or 3 completed framp cards.</div><br />
                                            <div className="text-sm">Winning a game of Lobby Burst for the first time will also award you with the "Main Lobby" profile picture background and the "Bubbles, Darling!" achievement!</div>
                                        </div>
                                    </div>
                                </div>
                            </ContentBox>
                        </div>
                    </div>
                </div>

                {/* ── Right column ── */}
                <div className="w-full xl:w-72 flex-shrink-0">

                    <ContentBox header="Latest Score Submissions" theme="battleball" tint="#cfcfcf">
                        <div className="p-1 min-h-[640px] max-h-[640px] overflow-y-auto">
                            {submissions.length === 0 ? (
                                <div className="text-coal/30 text-sm text-center pt-4">No submissions yet</div>
                            ) : submissions.map((s, i) => (
                                <div key={i} className="flex items-center justify-between py-2.5 border-b border-black/10 last:border-0 gap-2">
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-coal text-sm font-medium truncate">{s.habboName}</span>
                                        <span className="text-coal/40 text-xs uppercase tracking-wide">
                                            {s.outcome === 'win' ? 'Winner' : 'Game Over'}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-end flex-shrink-0">
                                        <span className="text-coal text-sm tabular-nums">{s.score.toString().padStart(6, '0')}</span>
                                        <span className="text-coal/40 text-xs">{s.stampsAwarded} framp{s.stampsAwarded !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ContentBox>

                    <ContentBox header="Note" theme="battleball" tint="red">
                        <div className="p-1 overflow-y-auto">
                            <div className="text-coal/70 text-sm">
                                Lobby Burst is in BETA. It should be compatible with all browsers, including mobile. Mobile users may experience some lag during large amounts of bubble pops which can be improved by muting the game audio. We're hoping to improve mobile performance soon.
                            </div>
                        </div>
                    </ContentBox>

                </div>

            </div>

            {/* ── Submit score modal ── */}
            {submitModalOpen && pendingScoreId && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: 'rgba(0,0,0,0.78)',
                        backdropFilter: 'blur(6px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: "'Jersey 10', monospace", // Font applied to backdrop for inheritance
                    }}
                >
                    <div
                        className="text-xl" // Tailwind size applied to the modal box
                        style={{
                            background: '#0d0b1a',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 12,
                            padding: '32px 40px',
                            maxWidth: 420, width: '90vw',
                            boxShadow: '0 8px 48px rgba(0,0,0,0.8)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24,
                            color: 'white' // Default text color
                        }}
                    >
                        <div style={{ color: 'rgba(255,255,255,0.3)', letterSpacing: '0.22em', textTransform: 'uppercase' }}>
                            Submit Score
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }}>
                            {[
                                {
                                    label: 'Score',
                                    value: score.toString().padStart(6, '0'),
                                    valueStyle: { color: 'white' },
                                },
                                {
                                    label: 'Outcome',
                                    value: phase === 'win' ? 'Winner!' : 'Game Over',
                                    valueStyle: { color: phase === 'win' ? '#ffd700' : '#ff6b6b' },
                                },
                                {
                                    label: 'Framps',
                                    value: phase === 'win'
                                        ? `${Math.max(1, Math.ceil(score / 100))} Framp${Math.max(1, Math.ceil(score / 100)) !== 1 ? 's' : ''}`
                                        : '1–3 Framps (random)',
                                    valueStyle: { color: '#a78bfa' },
                                },
                            ].map(({ label, value, valueStyle }, i, arr) => (
                                <div
                                    key={label}
                                    style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '12px 0',
                                        borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                                    }}
                                >
                                    <span style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>{label}</span>
                                    <span style={valueStyle}>{value}</span>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                            <button
                                onClick={() => setSubmitModalOpen(false)}
                                disabled={submitting}
                                className="text-xl" // Applied Tailwind size
                                style={{
                                    flex: 1, padding: '10px 0', borderRadius: 6,
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    background: 'transparent',
                                    color: 'rgba(255,255,255,0.45)',
                                    cursor: 'pointer',
                                    letterSpacing: '0.12em', textTransform: 'uppercase',
                                    fontFamily: 'inherit',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="text-xl" // Applied Tailwind size
                                style={{
                                    flex: 1, padding: '10px 0', borderRadius: 6,
                                    background: submitting ? 'rgba(100,50,200,0.4)' : 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: 'white',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    letterSpacing: '0.12em', textTransform: 'uppercase',
                                    fontFamily: 'inherit',
                                    opacity: submitting ? 0.65 : 1,
                                }}
                            >
                                {submitting ? 'Submitting...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}